/* Chat page component */
import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { useChatStream } from '../hooks/useChatStream';
import { useRagChatStream } from '../hooks/useRagChatStream';
import { useRestRagChatStream } from '../hooks/useRestRagChatStream';
import { useNonStreamingRagChat } from '../hooks/useNonStreamingRagChat';
import { useAccount } from '@azure/msal-react';
import { ChatInput } from '../components/Chat/ChatInput';
import ImageUsageAlert from '../components/Chat/ImageUsageAlert';
import TranslationQuotaAlert from '../components/Chat/TranslationQuotaAlert';
import { NetworkWarning } from '../components/Chat/NetworkWarning';
import { useMsal } from '@azure/msal-react';
import { createChatService, ChatService } from '../utils/chat.service';
import MessageContentRenderer from '../components/Chat/MessageContentRenderer';
import { downloadMessageAsDocument, downloadChatHistory, convertMarkdownTablesToHTML, printDomElementAsPDF, createJiraCardDocument } from '../utils/documentGeneration.service';
import { ChatContentItem } from '../utils/chat.service';
import ReasoningDropdown from '../components/Chat/ReasoningDropdown';
import { ChevronDownIcon, Pencil1Icon, CopyIcon, CheckIcon, DownloadIcon, ReloadIcon, FileTextIcon } from '@radix-ui/react-icons';
import { RiFolderDownloadLine } from "react-icons/ri";
import { BiCopy } from 'react-icons/bi';
import { FaRegFilePdf } from 'react-icons/fa';
import { BsFiletypeDocx } from 'react-icons/bs';
import { getUserInitials, getUserBubbleColor, computeBubbleTextColor } from '../utils/userUtils';
import { ThemeDialogTrigger } from '../components/Dialogs/ThemeDialogTrigger';
import { ConfirmationDialog } from '../components/Dialogs/ConfirmationDialog';
import { useTheme } from '../components/Theme/ThemeProvider';
import { useNetworkSpeed } from '../hooks/useNetworkSpeed';
import { useDeviceDetection } from '../hooks/useDeviceDetection';

import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/store';
import { clearaskChatHistory, clearRagHistory, cancelTranscribingMessages, setRagChatType } from '../store/chatModeSlice';
import { setTranslationQuota, clearUploadedFiles } from '../store/chatSlice';
import { setVoiceModeActive, setVoiceModeListening, setVoiceModePaused, setVoiceModeRestarting, setShowInactivityWarning, setInactivityCountdown, setDisconnectedByInactivity } from '../store/voiceModeSlice';
import { voiceModeService } from '../utils/voiceMode.service';
import logger from './../utils/logger';
import { RestRagType } from '../utils/restRagChat.service';
import FunctionCallStatus from '../components/Chat/FunctionCallStatus';
import SourcesList from '../components/Chat/SourcesList';
import JiraMarkdown from '../components/Chat/JiraMarkdown';
import SmartSuggestion from '../components/Chat/SmartSuggestion';
import { extractFirstValidPMO } from '../utils/extractFirstValidPMO';

const CopyIconWrapper: React.FC<{ size?: number }> = ({ size = 16 }) => {
  const icon = React.createElement(BiCopy as any, { size });
  return React.createElement('span', { style: { display: 'inline-flex', lineHeight: 0 } }, icon) as React.ReactElement;
};

const DownloadIconWrapper: React.FC<{ size?: number }> = ({ size = 16 }) => {
  const icon = React.createElement(RiFolderDownloadLine as any, { size });
  return React.createElement('span', { style: { display: 'inline-flex', lineHeight: 0 } }, icon) as React.ReactElement;
};


// Component responsible for streaming cursor positioning without causing layout jitter
const StreamingResponseContainer: React.FC<{currentResponse: string; isStreaming: boolean; isImageStreaming: boolean; mode: string; currentTools?: string[];}> = ({ currentResponse, isStreaming, isImageStreaming, mode }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const cursorRef = React.useRef<HTMLSpanElement | null>(null);
  // Don't show cursor for loading states, images, or empty/whitespace-only content
  const isLoadingMessage = currentResponse.trim() === 'Searching the web...' || 
                           currentResponse.trim().startsWith('Searching the web') ||
                           currentResponse.trim() === 'Thinking...' ||
                           currentResponse.trim().startsWith('Thinking');
  const hasSubstantialContent = currentResponse.trim().length > 0 && !isLoadingMessage;
  const showStreamingCursor = isStreaming && !isImageStreaming && hasSubstantialContent && !currentResponse.includes('![Generated Image]');

  // Utility: find last non-whitespace text node
  const getLastTextNode = React.useCallback((root: HTMLElement): Text | null => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => /\S/.test(node.nodeValue || '') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT
    });
    let last: Text | null = null;
    let n = walker.nextNode();
    while (n) {
      last = n as Text;
      n = walker.nextNode();
    }
    return last;
  }, []);

  React.useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // Ensure single cursor element exists (absolute positioned)
    if (!cursorRef.current) {
      const c = document.createElement('span');
      c.className = 'streaming-cursor-abs';
      c.style.position = 'absolute';
      c.style.width = '2px';
      c.style.background = mode === 'dark' ? '#9ca3af' : 'var(--color-accent)';
      c.style.animation = 'cursor-blink 1s infinite';
      c.style.pointerEvents = 'none';
      cursorRef.current = c;
      el.appendChild(c);
    }
    const cursorEl = cursorRef.current!;
    if (!showStreamingCursor) {
      cursorEl.style.display = 'none';
      return;
    }
  // Update color if theme changed
  cursorEl.style.background = mode === 'dark' ? '#9ca3af' : 'var(--color-accent)';
  cursorEl.style.display = 'inline-block';
    // Measure last text node position using Range
    const lastText = getLastTextNode(el);
    if (lastText) {
      const range = document.createRange();
      range.setStart(lastText, lastText.textContent ? lastText.textContent.length : 0);
      range.collapse(true);
      const rects = range.getClientRects();
      const rect = rects.length ? rects[rects.length - 1] : range.getBoundingClientRect();
      const containerRect = el.getBoundingClientRect();
      // Position cursor at end of line
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
      const top = rect.bottom - containerRect.top - lineHeight; // approximate baseline to top of line
      const left = rect.right - containerRect.left;
      cursorEl.style.top = `${top}px`;
      cursorEl.style.left = `${left}px`;
      cursorEl.style.height = `${lineHeight}px`;
    } else {
      // Fallback: place at start
      cursorEl.style.top = '0px';
      cursorEl.style.left = '0px';
      cursorEl.style.height = `${parseFloat(getComputedStyle(el).lineHeight) || 20}px`;
    }
  }, [currentResponse, showStreamingCursor, getLastTextNode, mode]);
  
  return (
    <div className={`flex-1 px-4 py-2 rounded-lg ${
      mode === 'dark'
        ? 'bg-transparent'
        : 'bg-[var(--color-secondary)]'
    }`} style={{ minWidth: 0, overflowX: 'hidden' }}>
      <div ref={containerRef} className="text-sm text-[var(--color-text)] relative" style={{ minWidth: 0, overflowY: 'hidden', border: 'none' }}>
        <MessageContentRenderer 
          content={currentResponse} 
          isStreaming={true} 
        />

      </div>
    </div>
  );
};

// Helper function to convert markdown tables to HTML tables
interface ChatPageProps {
  initialMessage?: string;
  initialTools?: string[];
  onBackToMain?: () => void;
  onHasMessagesChange?: (hasMessages: boolean) => void;
}

const ChatPage: React.FC<ChatPageProps> = ({ initialMessage, initialTools, onBackToMain, onHasMessagesChange }) => {
  const account = useAccount();
  const { mode } = useTheme();
  const { isMobile } = useDeviceDetection();
  const dispatch = useDispatch();
  const chatModeState = useSelector((state: RootState) => state.chatMode);
  const voiceModeState = useSelector((state: RootState) => state.voiceMode);
  const { isActive: isVoiceModeActive, inactivityCountdown } = voiceModeState;

  // Update browser tab title with inactivity countdown so it's visible even when the tab is in the background
  React.useEffect(() => {
    if (isVoiceModeActive && inactivityCountdown > 0) {
      document.title = `3M Navigator (${inactivityCountdown}s)`;
    } else {
      document.title = '3M Navigator';
    }
    return () => {
      document.title = '3M Navigator';
    };
  }, [isVoiceModeActive, inactivityCountdown]);
  
  // RAG mode is active only when mode is explicitly '3M Knowledge'.
  // ragChatType is preserved across mode switches as a memory of the last selected RAG
  // but does NOT make us "in RAG mode" when mode is 'Ask Chat'.
  const isRagMode = chatModeState.mode === '3M Knowledge';
  const isRestRagMode = isRagMode && (chatModeState.ragChatType === 'Legal' || chatModeState.ragChatType === 'DevOps' || chatModeState.ragChatType === 'TEBG' || chatModeState.ragChatType === 'ITSM' || chatModeState.ragChatType === 'JIRA');
  const isWebSocketRagMode = isRagMode && chatModeState.ragChatType === 'Everyday Insights';

  // Image usage tracking (temporary client-side until API provides remaining quota)
  const DAILY_LIMIT = 3; // IMAGE DAILY LIMIT
  const IMAGE_USAGE_KEY = 'dailyImageUsage';
  const [imageUsageCount, setImageUsageCount] = React.useState<number>(0);
  const [showImageUsageAlert, setShowImageUsageAlert] = React.useState(false);
  const lastImageMessageCountRef = React.useRef(0);
  const usageDateRef = React.useRef<string>('');
  const [imageQuotaLimit, setImageQuotaLimit] = React.useState<number>(DAILY_LIMIT);
  const [imageQuotaRemaining, setImageQuotaRemaining] = React.useState<number | undefined>(undefined);
  const [imageQuotaResetUtc, setImageQuotaResetUtc] = React.useState<string | undefined>(undefined);
  const { instance: msalInstance } = useMsal();
  const chatServiceRef = React.useRef<ReturnType<typeof createChatService> | null>(null);
  if (!chatServiceRef.current && msalInstance) {
    chatServiceRef.current = createChatService(msalInstance);
  }
  const {translationQuotaDetails, isTranslating, translationHistory, translationError} = useSelector((state: RootState) => state.chat);
  
  // custom hook call for reusability and cleanliness
  // Use different chat implementations based on mode
  const standardChat = useChatStream({
    onError: (error) => {
      console.error('Chat error:', error);
    }
  });
  
  // Only initialize WebSocket RAG when it's the active mode
  const ragChat = useRagChatStream({
    msalInstance,
    onError: (error) => {
      console.error('RAG chat error:', error);
    },
  });
  
  // REST RAG hooks for Legal, DevOps, TEBG, ITSM - only initialize the active one
  const legalChat = useRestRagChatStream({
    ragType: 'Legal' as RestRagType,
    onError: (error: Error) => {
      console.error('Legal RAG chat error:', error);
    }
  });
  
  const devOpsChat = useRestRagChatStream({
    ragType: 'DevOps' as RestRagType,
    onError: (error: Error) => {
      console.error('DevOps RAG chat error:', error);
    }
  });
  
  const tebgChat = useRestRagChatStream({
    ragType: 'TEBG' as RestRagType,
    onError: (error: Error) => {
      console.error('TEBG RAG chat error:', error);
    }
  });
  
  const itsmChat = useRestRagChatStream({
    ragType: 'ITSM' as RestRagType,
    onError: (error: Error) => {
      console.error('ITSM RAG chat error:', error);
    }
  });

  const jiraChat = useNonStreamingRagChat({
    onError: (error: Error) => {
      console.error('JIRA RAG chat error:', error);
    }
  });
  
  // Select the appropriate chat interface based on mode and RAG type
  const {
    isStreaming,
    isImageStreaming,
    error,
    currentResponse,
    chatHistory,
    currentTools,
    debugInfo,
    reasoningContent,
    reasoningActive,
    thinkingContent, // Web search thinking trace
    thinkingComplete, // Whether thinking section is done
    sendMessage: rawSendMessage,
    stopStream,
    clearChat,
    removeStoppedMessages,
    addMessage,
  } = isRestRagMode ? {
    // REST RAG mode: use appropriate REST RAG chat hook based on ragChatType
    ...(chatModeState.ragChatType === 'Legal' ? legalChat :
        chatModeState.ragChatType === 'DevOps' ? devOpsChat :
        chatModeState.ragChatType === 'TEBG' ? tebgChat :
        chatModeState.ragChatType === 'JIRA' ? jiraChat :
        itsmChat),
    isStreaming: chatModeState.ragChatType === 'Legal' ? legalChat.isStreaming :
                 chatModeState.ragChatType === 'DevOps' ? devOpsChat.isStreaming :
                 chatModeState.ragChatType === 'TEBG' ? tebgChat.isStreaming :
                 chatModeState.ragChatType === 'JIRA' ? jiraChat.isStreaming :
                 itsmChat.isStreaming,
    isImageStreaming: false,
    error: chatModeState.ragChatType === 'Legal' ? legalChat.error :
           chatModeState.ragChatType === 'DevOps' ? devOpsChat.error :
           chatModeState.ragChatType === 'TEBG' ? tebgChat.error :
           chatModeState.ragChatType === 'JIRA' ? jiraChat.error :
           itsmChat.error,
    currentResponse: chatModeState.ragChatType === 'Legal' ? legalChat.currentResponse :
                     chatModeState.ragChatType === 'DevOps' ? devOpsChat.currentResponse :
                     chatModeState.ragChatType === 'TEBG' ? tebgChat.currentResponse :
                     chatModeState.ragChatType === 'JIRA' ? jiraChat.currentResponse :
                     itsmChat.currentResponse,
    chatHistory: chatModeState.ragChatType === 'Legal' ? legalChat.chatHistory :
                 chatModeState.ragChatType === 'DevOps' ? devOpsChat.chatHistory :
                 chatModeState.ragChatType === 'TEBG' ? tebgChat.chatHistory :
                 chatModeState.ragChatType === 'JIRA' ? jiraChat.chatHistory :
                 itsmChat.chatHistory,
    currentTools: undefined,
    debugInfo: undefined,
    reasoningContent: undefined,
    reasoningActive: undefined,
    thinkingContent: undefined,
    thinkingComplete: undefined,
    sendMessage: chatModeState.ragChatType === 'Legal' ? legalChat.sendMessage :
                 chatModeState.ragChatType === 'DevOps' ? devOpsChat.sendMessage :
                 chatModeState.ragChatType === 'TEBG' ? tebgChat.sendMessage :
                 chatModeState.ragChatType === 'JIRA' ? jiraChat.sendMessage :
                 itsmChat.sendMessage,
    stopStream: chatModeState.ragChatType === 'Legal' ? legalChat.cancelRequest :
                chatModeState.ragChatType === 'DevOps' ? devOpsChat.cancelRequest :
                chatModeState.ragChatType === 'TEBG' ? tebgChat.cancelRequest :
                chatModeState.ragChatType === 'JIRA' ? jiraChat.cancelRequest :
                itsmChat.cancelRequest,
    clearChat: chatModeState.ragChatType === 'Legal' ? legalChat.clearHistory :
               chatModeState.ragChatType === 'DevOps' ? devOpsChat.clearHistory :
               chatModeState.ragChatType === 'TEBG' ? tebgChat.clearHistory :
               chatModeState.ragChatType === 'JIRA' ? jiraChat.clearHistory :
               itsmChat.clearHistory,
    removeStoppedMessages: undefined,
    addMessage: undefined,
  } : isWebSocketRagMode ? {
    // WebSocket RAG mode: use WebSocket RAG chat hook
    isStreaming: ragChat.isStreaming,
    isImageStreaming: false,
    error: ragChat.error,
    currentResponse: ragChat.currentResponse,
    chatHistory: ragChat.chatHistory,
    currentTools: undefined,
    debugInfo: undefined,
    reasoningContent: undefined,
    reasoningActive: undefined,
    thinkingContent: undefined,
    thinkingComplete: undefined,
    sendMessage: (msg: string) => ragChat.sendMessage(msg),
    stopStream: () => ragChat.stopStream(),
    clearChat: ragChat.clearChat,
    removeStoppedMessages: undefined,
    addMessage: undefined,
  } : {
    // Standard mode: use standard chat hook
    isStreaming: standardChat.isStreaming,
    isImageStreaming: standardChat.isImageStreaming,
    error: standardChat.error,
    currentResponse: standardChat.currentResponse,
    chatHistory: standardChat.chatHistory,
    currentTools: standardChat.currentTools,
    debugInfo: standardChat.debugInfo,
    reasoningContent: standardChat.reasoningContent,
    reasoningActive: standardChat.reasoningActive,
    thinkingContent: standardChat.thinkingContent,
    thinkingComplete: standardChat.thinkingComplete,
    sendMessage: standardChat.sendMessage,
    stopStream: standardChat.stopStream,
    clearChat: standardChat.clearChat,
    removeStoppedMessages: standardChat.removeStoppedMessages,
    addMessage: standardChat.addMessage,
  };
  
  // Wrapper for sendMessage that handles tools parameter based on mode
  // REST RAG modes (Legal, TEBG, ITSM, JIRA) don't support tools parameter
  const sendMessage = React.useCallback(async (message: string, tools?: string[]) => {
    if (isRestRagMode || isWebSocketRagMode) {
      // REST RAG and WebSocket RAG modes: ignore tools parameter
      return await rawSendMessage(message);
    } else {
      // Regular chat mode: pass tools parameter
      return await (rawSendMessage as any)(message, tools);
    }
  }, [isRestRagMode, isWebSocketRagMode, rawSendMessage]);

  // When user accepts Legal switch, auto re-send the same question in Legal mode.
  const [pendingLegalResend, setPendingLegalResend] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!pendingLegalResend) return;
    const isLegalMode = chatModeState.mode === '3M Knowledge' && chatModeState.ragChatType === 'Legal';
    if (!isLegalMode) return;
    if (isStreaming) return;

    sendMessage(pendingLegalResend);
    setPendingLegalResend(null);
  }, [pendingLegalResend, chatModeState.mode, chatModeState.ragChatType, isStreaming, sendMessage]);
  
  // Merge voice messages from Redux into chatHistory when voice mode is active
  const voiceHistory = isRagMode ? chatModeState.ragHistory : chatModeState.askChatHistory;
  // Wrap clearChat to also clear voice history
  const clearChatWithVoice = React.useCallback(() => {
    clearChat();
    // Clear voice history for current mode
    if (chatModeState.mode === 'Ask Chat') {
      dispatch(clearaskChatHistory());
    } else {
      dispatch(clearRagHistory());
    }
  }, [clearChat, chatModeState.mode, dispatch]);

  // Stop any active stream, voice mode, and clear uploaded files when switching between Ask Chat / 3M Knowledge tabs
  const prevModeRef = React.useRef(chatModeState.mode);
  React.useEffect(() => {
    if (prevModeRef.current !== chatModeState.mode) {
      prevModeRef.current = chatModeState.mode;
      if (isStreaming) {
        stopStream();
      }
      if (isVoiceModeActive) {
        dispatch(cancelTranscribingMessages());
        voiceModeService.disconnect();
        dispatch(setVoiceModeActive(false));
        dispatch(setVoiceModeListening(false));
        dispatch(setVoiceModePaused(false));
        dispatch(setVoiceModeRestarting(false));
        dispatch(setShowInactivityWarning(false));
        dispatch(setInactivityCountdown(0));
        dispatch(setDisconnectedByInactivity(false));
      }
      dispatch(clearUploadedFiles());
    }
  }, [chatModeState.mode, isStreaming, stopStream, isVoiceModeActive, dispatch]);
  
  const displayChatHistory = React.useMemo(() => {
    // Translation mode takes precedence — show translation results even when voice messages
    // are still present in askChatHistory (e.g. left over from a mid-response voice stop).
    if (translationHistory.length > 0) {
      return translationHistory;
    }

    // Filter for voice messages only
    const voiceMessages = voiceHistory.filter(msg => msg.isVoice);
    
    // If we have voice messages, merge them with regular chat history
    if (voiceMessages.length > 0) {
      // Combine and sort by message ID (which includes timestamp)
      const combined = [...chatHistory, ...voiceMessages];
      // Sort by ID to maintain chronological order
      // Voice message IDs are in format: voice-user-{timestamp} or voice-assistant-{timestamp}
      return combined.sort((a, b) => {
        // Extract timestamp from message IDs for chronological ordering
        const extractTime = (id: string | undefined) => {
          if (!id) return 0;
          
          // For voice messages: voice-user-{timestamp} or voice-assistant-{timestamp}
          if (id.startsWith('voice-user-') || id.startsWith('voice-assistant-')) {
            const parts = id.split('-');
            return parseInt(parts[parts.length - 1] || '0');
          }
          
          // For regular messages: {timestamp}-{random} (e.g., "1739009999999-0.123456")
          // Extract the timestamp before the first hyphen
          const match = id.match(/^(\d+)-/);
          if (match) {
            return parseInt(match[1]);
          }
          
          // Fallback: try to extract any leading number
          const leadingMatch = id.match(/^(\d+)/);
          return leadingMatch ? parseInt(leadingMatch[1]) : 0;
        };
        
        const aTime = extractTime(a.id);
        const bTime = extractTime(b.id);
        return aTime - bTime;
      });
    }

    return chatHistory;
  }, [chatHistory, translationHistory, voiceHistory]);

  // Notify parent when message presence changes so Header can gate its confirmation dialog
  React.useEffect(() => {
    onHasMessagesChange?.(displayChatHistory.length > 0);
  }, [displayChatHistory.length, onHasMessagesChange]);

  // isStreaming is now synced to Redux inside useChatStream directly
  
  // TEST: Expose test function to window for easy testing
  React.useEffect(() => {
    logger.log('🧪 [TEST SETUP] Mounting test functions to window...');
    
    // Expose ChatService test methods to window
    (window as any).testQuotaError = () => {
      logger.log('🧪 Enabling quota error test mode. Next image generation will fail with quota error.');
      logger.log('🔔 IMPORTANT: You MUST select the Image Generation tool when sending the message!');
      logger.log('   1. Click the "+" button or tools dropdown');
      logger.log('   2. Select "Image Generation"');
      logger.log('   3. Type your message (e.g., "Generate an image")');
      logger.log('   4. Send the message');
      ChatService.enableQuotaErrorTest();
      logger.log('✅ Test mode enabled. Flag set to:', (ChatService as any).TEST_QUOTA_ERROR);
    };
    
    (window as any).disableQuotaErrorTest = () => {
      ChatService.disableQuotaErrorTest();
    };
    
    (window as any).checkTestMode = () => {
      logger.log('=== TEST MODE STATUS ===');
      ChatService.getTestModeStatus();
      logger.log('========================');
    };
    
    logger.log('✅ Test functions loaded successfully:');
    logger.log('  - testQuotaError() - Enable test mode, then send image generation request WITH IMAGE TOOL SELECTED');
    logger.log('  - disableQuotaErrorTest() - Disable test mode');
    logger.log('  - checkTestMode() - Check current test mode status');
    logger.log('  - window.testQuotaError =', typeof (window as any).testQuotaError);
  }, []);

  const [inputValue, setInputValue] = React.useState(initialMessage || '');
  const hasProcessedInitialMessage = React.useRef(false);
  
  // User bubble color state - watch localStorage for changes
  const [userBubbleColor, setUserBubbleColor] = React.useState(() => getUserBubbleColor());
  
  // Copy feedback state
  const [justCopied, setJustCopied] = React.useState<string | null>(null);
  // Tracks copy feedback for messages whose content is null (e.g. voice transcription unavailable),
  // since justCopied compares by content string and can never match null.
  const [justCopiedByIndex, setJustCopiedByIndex] = React.useState<number | null>(null);
  
  // Assistant message copy state (permanent until new message)
  const [copiedAssistantMessages, setCopiedAssistantMessages] = React.useState<Set<number>>(new Set());
  
  // Download overlay state
  const [showDownloadOverlay, setShowDownloadOverlay] = React.useState<number | null>(null);
  const [showJiraFormatOverlay, setShowJiraFormatOverlay] = React.useState(false);
  const [downloadOverlayLocked, setDownloadOverlayLocked] = React.useState(false);
  const hideOverlayTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const downloadOverlayRef = React.useRef<HTMLDivElement | null>(null);
  const portalDropdownRef = React.useRef<HTMLDivElement | null>(null);
  const [downloadButtonRect, setDownloadButtonRect] = React.useState<DOMRect | null>(null);
  
  // Network speed detection
  const { isSlowConnection } = useNetworkSpeed();
  const [showNetworkWarning, setShowNetworkWarning] = React.useState(false);
  // Session-only dismissal using sessionStorage (persists across chats, resets on browser/tab close)
  const [networkWarningDismissed, setNetworkWarningDismissed] = React.useState(() => {
    try {
      return sessionStorage.getItem('networkWarningDismissed') === 'true';
    } catch {
      return false;
    }
  });
  
  // Show network warning when slow connection is detected and user has sent at least one message
  React.useEffect(() => {
    if (isSlowConnection && chatHistory.length > 0 && !networkWarningDismissed) {
      setShowNetworkWarning(true);
    }
  }, [isSlowConnection, chatHistory.length, networkWarningDismissed]);
  
  // Cleanup timeout on unmount
  React.useEffect(() => {
    const timeout = hideOverlayTimeoutRef.current;
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, []);

  // Click outside to close locked overlay
  React.useEffect(() => {
    if (!downloadOverlayLocked) return;
    const handleClickOutside = (e: MouseEvent) => {
      const insideWrapper = downloadOverlayRef.current?.contains(e.target as Node);
      const insidePortal = portalDropdownRef.current?.contains(e.target as Node);
      if (!insideWrapper && !insidePortal) {
        setShowDownloadOverlay(null);
        setShowJiraFormatOverlay(false);
        setDownloadOverlayLocked(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [downloadOverlayLocked]);

  // Close the portal dropdown whenever the chat scroll container scrolls —
  // the portal is position:fixed so it would otherwise float away from the button.
  React.useEffect(() => {
    if (showDownloadOverlay === null) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    const close = () => {
      setShowDownloadOverlay(null);
      setShowJiraFormatOverlay(false);
      setDownloadOverlayLocked(false);
    };
    el.addEventListener('scroll', close, { passive: true });
    return () => el.removeEventListener('scroll', close);
  }, [showDownloadOverlay]);
  
  // Listen for localStorage changes to update user bubble color
  React.useEffect(() => {
    const handleStorageChange = () => {
      setUserBubbleColor(getUserBubbleColor());
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for manual updates within the same tab
    const interval = setInterval(() => {
      const currentColor = getUserBubbleColor();
      setUserBubbleColor(prev => prev !== currentColor ? currentColor : prev);
    }, 100);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);
  
  // Scroll management state
  // We want it to auto-scroll as content streams in but NOT if the user purposefully scrolls up.
  // If someone scrolls up, instead, we have a "scroll to bottom" button that indicates there are new unread messages
  // the arrows on this button will ripple as content streams in and then be still once the content is done streaming.
  // The arrow goes away once they've read any new messages, which means we also need to track if they've read them yet.
  const [shouldAutoScroll, setShouldAutoScroll] = React.useState(true);
  const [showScrollToBottom, setShowScrollToBottom] = React.useState(false);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const messagesContainerRef = React.useRef<HTMLDivElement>(null);
  const isUserScrollingRef = React.useRef(false);
  const lastScrollTop = React.useRef(0);
  const hasSeenNewContentRef = React.useRef(false); // Track if user has seen new content

  // Derived: Are we waiting for an assistant response? (last message is user and not streaming)
  const latestMessage = displayChatHistory[displayChatHistory.length - 1];
  const isWaitingForResponse = latestMessage && latestMessage.role === 'user' && !isStreaming;
  
  // New: Track if latest assistant message was a card
  const [latestAssistantWasCard, setLatestAssistantWasCard] = React.useState(false);

  // Dynamic input height state, height expands up to 5 lines so we need to readjust to account for this
  const [chatInputHeight, setChatInputHeight] = React.useState(128); // Default padding (pb-32 = 128px)
  const bottomOverlayRef = React.useRef<HTMLDivElement>(null);
  const disclaimerRef = React.useRef<HTMLDivElement>(null);
  const [bottomOverlayHeight, setBottomOverlayHeight] = React.useState<number>(0);
  const prevResponseLengthRef = React.useRef<number>(0);

  // Measure bottom overlay + disclaimer height dynamically
  React.useLayoutEffect(() => {
    const measure = () => {
      let total = 0;
      if (bottomOverlayRef.current) {
        total += bottomOverlayRef.current.offsetHeight;
      }
      if (disclaimerRef.current) {
        total += disclaimerRef.current.offsetHeight;
      }
  // Use exact measured height (no manual subtraction)
  setBottomOverlayHeight(total);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [chatInputHeight]);

  // Debug logging for currentResponse
  React.useEffect(() => {
    if (currentResponse && currentResponse.includes('![Generated Image]')) {      
      // Image debugging placeholder (removed detailed logging for cleanup)
    }
  }, [currentResponse]);

  // Utility functions for scroll management
  const scrollToBottom = React.useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
      // User manually scrolled to bottom, mark content as seen
      hasSeenNewContentRef.current = true;
      setShowScrollToBottom(false);
    }
  }, []);

  // Callback for when message content (with file previews) has finished loading
  // This is called after each individual preview loads (incremental scrolling)
  const handleMessageContentLoaded = React.useCallback((messageIndex: number) => {
    // Only scroll for the LAST assistant message in the chat
    const isLastMessage = messageIndex === displayChatHistory.length - 1;
    
    // DON'T scroll if currently streaming - let the streaming auto-scroll handle it
    // Only scroll after streaming completes when file previews finish loading
    if (isStreaming) {
      return;
    }
    
    // Scroll to bottom after each preview loads (incremental scrolling)
    // BUT only if:
    // 1. This is the last message in the chat
    // 2. User hasn't scrolled up (respect shouldAutoScroll)
    if (isLastMessage && shouldAutoScroll && !isUserScrollingRef.current) {
      // Use requestAnimationFrame for smooth incremental scrolling
      // No setTimeout needed - immediate scroll after each preview
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          const { scrollHeight, clientHeight } = scrollContainerRef.current;
          if (scrollHeight > clientHeight) {
            // Use instant scroll (not smooth) for incremental updates to avoid jitter
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
          }
        }
      });
    }
  }, [displayChatHistory.length, shouldAutoScroll, isStreaming]);

  // Helper function for handleScroll to still continue or re-start auto scrolling to bottom
  const isNearBottom = React.useCallback(() => {
    if (!scrollContainerRef.current) return false; // we aren't currently scrolling so never need to check
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    return scrollHeight - scrollTop - clientHeight < 50; // Within 50px of bottom then continue the scroll to bottom behavior
  }, []);

  // ResizeObserver to auto-scroll when streaming messages expand in height
  React.useEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    const scrollContainer = scrollContainerRef.current;
    if (!messagesContainer || !scrollContainer) return;

    const resizeObserver = new ResizeObserver((entries) => {
      logger.log('[ResizeObserver] Triggered:', {
        isStreaming,
        isVoiceModeActive,
        shouldAutoScroll,
        isUserScrolling: isUserScrollingRef.current,
        contentHeight: entries[0]?.contentRect.height
      });

      // Suppress scroll-to-bottom for Jira RAG mode entirely
      if (chatModeState.ragChatType === 'JIRA') return;

      // Auto-scroll if either streaming OR voice mode is active (voice messages update chatHistory directly)
      // Prevent scroll if latest assistant message was a card
      if ((isStreaming || isVoiceModeActive) && shouldAutoScroll && !isUserScrollingRef.current && !latestAssistantWasCard ) {
        requestAnimationFrame(() => {
          if (scrollContainer) {
            logger.log('[ResizeObserver] Scrolling to bottom', isStreaming, isVoiceModeActive, shouldAutoScroll, isUserScrollingRef.current, latestAssistantWasCard);
            scrollContainer.scrollTop = scrollContainer.scrollHeight;
          }
        });
      }
    });

    resizeObserver.observe(messagesContainer);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isStreaming, isVoiceModeActive, shouldAutoScroll, latestAssistantWasCard, chatModeState.ragChatType]);

  // Handle scroll events to detect manual scrolling
  const handleScroll = React.useCallback(() => {
    if (!scrollContainerRef.current) return;
    const { scrollTop } = scrollContainerRef.current;
    // Check if user reached bottom, if so, mark content as seen
    if (isNearBottom()) {
      if (showScrollToBottom) {
        hasSeenNewContentRef.current = true;
        setShowScrollToBottom(false);
      }
      setShouldAutoScroll(true);
      isUserScrollingRef.current = false;
    } else {
      // Detect any upward scroll movement to disable auto-scroll immediately
      if (scrollTop < lastScrollTop.current) {
        // User scrolled up - disable auto-scroll immediately
        if (!isUserScrollingRef.current) {
          isUserScrollingRef.current = true;
          setShouldAutoScroll(false);
        }
        // Show scroll button if user scrolls up while waiting for a response (even before streaming)
        if (!hasSeenNewContentRef.current && (isStreaming || isWaitingForResponse || currentResponse)) {
          setShowScrollToBottom(true);
        }
      }
    }
    lastScrollTop.current = scrollTop;
  }, [isNearBottom, showScrollToBottom, isStreaming, currentResponse, isWaitingForResponse]);

  // Send initial message if provided (only should run once), we can't put it in the regular sendMessage because that function might not be fully mounted yet
  // we just want it to run on initial render, not multiple times and since it's coming from a different component we need this useEffect
  useEffect(() => {
    if (initialTools?.includes('translation')) {
      hasProcessedInitialMessage.current = true;
      setInputValue('');
    } else if (initialMessage && !hasProcessedInitialMessage.current && chatHistory.length === 0 && !isStreaming) {
      hasProcessedInitialMessage.current = true;
      sendMessage(initialMessage, initialTools);
      setInputValue('');
    }
  }, [initialMessage, initialTools, chatHistory.length, isStreaming, sendMessage]);

  // Load existing usage from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(IMAGE_USAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        const today = new Date().toISOString().slice(0, 10);
        if (parsed.date === today) {
          setImageUsageCount(parsed.count || 0);
          usageDateRef.current = parsed.date;
        } else {
          // Different day - reset
          usageDateRef.current = today;
          localStorage.setItem(IMAGE_USAGE_KEY, JSON.stringify({ date: today, count: 0 }));
        }
      } else {
        const today = new Date().toISOString().slice(0, 10);
        usageDateRef.current = today;
        localStorage.setItem(IMAGE_USAGE_KEY, JSON.stringify({ date: today, count: 0 }));
      }
    } catch (e) {
      // Fallback safe initialization
      const today = new Date().toISOString().slice(0, 10);
      usageDateRef.current = today;
    }
  }, []);

  // Detect new assistant image messages and increment usage
  useEffect(() => {
    // Count assistant messages containing generated image marker
  const imageMessages = (chatHistory as any[]).filter((m: any) => m.role === 'assistant' && typeof m.content === 'string' && m.content.includes('![Generated Image]'));
    if (imageMessages.length > lastImageMessageCountRef.current) {
      const newImages = imageMessages.length - lastImageMessageCountRef.current;
      lastImageMessageCountRef.current = imageMessages.length;
      // Ensure date current
      const today = new Date().toISOString().slice(0, 10);
      if (usageDateRef.current !== today) {
        usageDateRef.current = today;
        setImageUsageCount(0);
      }
      setImageUsageCount(prev => {
        const baseLimit = imageQuotaLimit || DAILY_LIMIT;
        const updated = Math.min(prev + newImages, baseLimit);
        try {
          localStorage.setItem(IMAGE_USAGE_KEY, JSON.stringify({ date: usageDateRef.current, count: updated }));
        } catch {}
        // Trigger alert each time we actually increase
        if (updated !== prev) {
          setShowImageUsageAlert(true);
        }
        return updated;
      });
    }
    }, [chatHistory, imageQuotaLimit]);

  // Fetch image quota from server when image generation starts - this data will be displayed in the alert when generation completes
  useEffect(() => {
    if (!isImageStreaming) {
      return;
    }
    let cancelled = false;
    const fetchUpdatedQuota = async () => {
      try {
        if (!chatServiceRef.current) return;
        
        const quotaResp = await chatServiceRef.current.getImageQuota();
        
        if (cancelled) return;
        
        if (quotaResp.success && quotaResp.quota) {
          logger.log('QUOTA: ' + JSON.stringify(quotaResp.quota));
          setImageQuotaLimit(quotaResp.quota.quota_limit);
          setImageUsageCount(quotaResp.quota.current_usage);
          setImageQuotaRemaining(quotaResp.quota.remaining);
          setImageQuotaResetUtc(quotaResp.quota.reset_time);
        }
      } catch (err) {
        /* suppress quota errors */
      }
    };
    fetchUpdatedQuota();
    return () => { cancelled = true; };
  }, [isImageStreaming]);

  // Auto-scroll during streaming if shouldAutoScroll is true (true if user didn't scroll up OR user scrolled back down)
  useEffect(() => {
    // Suppress scroll-to-bottom for Jira RAG mode entirely
    if (chatModeState.ragChatType === 'JIRA') return;
    if (isStreaming && currentResponse && shouldAutoScroll) {
      if (scrollContainerRef.current) {
        const { scrollHeight, clientHeight } = scrollContainerRef.current;
        if (scrollHeight > clientHeight) {
          requestAnimationFrame(() => {
            if (!scrollContainerRef.current) return;
            const offset = bottomOverlayHeight || (chatInputHeight + 32 - 12);
            const targetScroll = scrollContainerRef.current.scrollHeight - offset;
            scrollContainerRef.current.scrollTop = targetScroll;
          });
        }
      }
    }
    // Note: Don't automatically show scroll button here - only show when user manually scrolls up
  }, [currentResponse, isStreaming, shouldAutoScroll, chatInputHeight, bottomOverlayHeight, chatModeState.ragChatType]);

  // Track previous response length
  useEffect(() => {
    prevResponseLengthRef.current = currentResponse ? currentResponse.length : 0;
  }, [currentResponse, isStreaming]);

  // Multi-frame stabilization scroll after streaming completes to counter final layout shifts
  useEffect(() => {
    // Suppress scroll-to-bottom for Jira RAG mode entirely
    if (chatModeState.ragChatType === 'JIRA') return;
    if (isStreaming) return; // only run after stream ends
    if (!currentResponse) return;
    if (!shouldAutoScroll || isUserScrollingRef.current) return;
    if (latestAssistantWasCard) return; // Do NOT scroll to bottom if latest assistant message was a card
    // Run scroll on several consecutive animation frames + a timeout to catch late image/style reflows
    let frame = 0;
    const maxFrames = 5; // attempt for first ~5 RAF cycles
    const offset = bottomOverlayHeight || (chatInputHeight + 32 - 12);
    const performScroll = () => {
      if (!scrollContainerRef.current) return;
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight - offset;
      frame++;
      if (frame < maxFrames) {
        requestAnimationFrame(performScroll);
      }
    };
    requestAnimationFrame(performScroll);
    const lateTimeout = setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight - offset;
      }
    }, 250); // catch late renders (syntax highlighting, etc.)
    return () => clearTimeout(lateTimeout);
  }, [isStreaming, currentResponse, shouldAutoScroll, bottomOverlayHeight, chatInputHeight, latestAssistantWasCard, chatModeState.ragChatType]);

  // Reset scroll-to-bottom arrow when chat is cleared
  useEffect(() => {
    if (displayChatHistory.length === 0) {
      setShowScrollToBottom(false);
      setShouldAutoScroll(true);
      isUserScrollingRef.current = false;
      hasSeenNewContentRef.current = true;
    }
  }, [displayChatHistory.length]);

  // ResizeObserver to catch post-stream content height increases (markdown, images, highlighting)
  useEffect(() => {
    // Suppress scroll-to-bottom for Jira RAG mode entirely
    if (chatModeState.ragChatType === 'JIRA') return;
    if (isStreaming) return; // only after completion
    if (!currentResponse) return;
    if (!shouldAutoScroll || isUserScrollingRef.current) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    let settleTimeout: NodeJS.Timeout;
    const offset = bottomOverlayHeight || chatInputHeight;
    let lastHeight = container.scrollHeight;
    const observer = new ResizeObserver(() => {
      // Double-check user hasn't scrolled up since observer was created
      if (isUserScrollingRef.current || !shouldAutoScroll) {
        observer.disconnect();
        return;
      }
      const newHeight = container.scrollHeight;
      if (newHeight > lastHeight + 4) { // significant growth
        container.scrollTop = container.scrollHeight - offset;
        lastHeight = newHeight;
      }
      // Reset settle timer
      if (settleTimeout) clearTimeout(settleTimeout);
      settleTimeout = setTimeout(() => {
        observer.disconnect();
      }, 500); // stop observing after 500ms of no changes
    });
    observer.observe(container);
    // Initial adjustment just in case (only if user hasn't scrolled)
    if (!isUserScrollingRef.current && shouldAutoScroll) {
      container.scrollTop = container.scrollHeight - offset;
    }
    return () => {
      observer.disconnect();
      if (settleTimeout) clearTimeout(settleTimeout);
    };
  }, [isStreaming, currentResponse, shouldAutoScroll, bottomOverlayHeight, chatInputHeight, chatModeState.ragChatType]);

  // Final visibility correction ensuring last message not obscured
  useEffect(() => {
    // Suppress scroll-to-bottom for Jira RAG mode entirely
    if (chatModeState.ragChatType === 'JIRA') return;
    if (isStreaming) return;
    if (!shouldAutoScroll || isUserScrollingRef.current) return;
    if (!displayChatHistory.length) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const lastIndex = displayChatHistory.length - 1;
    const lastEl = container.querySelector(`[data-chat-index="${lastIndex}"]`);
    if (!lastEl) return;
    const offset = bottomOverlayHeight || chatInputHeight;
    let frames = 0;
    const maxFrames = 6;
    const step = () => {
      const rect = (lastEl as HTMLElement).getBoundingClientRect();
      const cRect = container.getBoundingClientRect();
      const visibleBottom = cRect.bottom - offset;
      if (rect.bottom > visibleBottom - 1) {
        container.scrollTop = container.scrollHeight - offset;
      }
      frames++;
      if (frames < maxFrames) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [isStreaming, displayChatHistory, shouldAutoScroll, bottomOverlayHeight, chatInputHeight, chatModeState.ragChatType]);

  // Additional effect to ensure continuous scrolling during streaming
  useEffect(() => {
    // Suppress scroll-to-bottom for Jira RAG mode entirely
    if (chatModeState.ragChatType === 'JIRA') return;
    let scrollInterval: NodeJS.Timeout | null = null;
    
    if (isStreaming && shouldAutoScroll) {
      // Set up interval to keep scrolling during active streaming
      scrollInterval = setInterval(() => {
        if (scrollContainerRef.current) {
          const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
          const offset = bottomOverlayHeight || (chatInputHeight + 32 - 12);
          const targetScrollBottom = scrollHeight - clientHeight - offset;
          const isAtBottom = scrollTop >= targetScrollBottom - 6; // tighter tolerance
          if (!isAtBottom) {
            scrollContainerRef.current.scrollTop = scrollHeight - offset;
          }
        }
      }, 50); // Check every 50ms during streaming
    }
    
    return () => {
      if (scrollInterval) {
        clearInterval(scrollInterval);
      }
    };
  }, [isStreaming, shouldAutoScroll, chatInputHeight, bottomOverlayHeight, chatModeState.ragChatType]);

  // Effect: Detect if latest assistant message is a card and update state
  // Only relevant for JIRA RAG — skip entirely in all other modes.
  useEffect(() => {
    if (chatModeState.ragChatType !== 'JIRA') return;
    const latestMessage = displayChatHistory[displayChatHistory.length - 1];
    if (latestMessage && latestMessage.role === 'assistant') {
      logger.log('[Card Detection Effect] latestMessage object:', latestMessage);
      let isCard = false;
      // Type guard for Jira RAG card detection
      const hasUiEvents =
        typeof latestMessage === 'object' &&
        latestMessage !== null &&
        'ui_events' in latestMessage &&
        Array.isArray((latestMessage as any).ui_events) &&
        (latestMessage as any).ui_events.length > 0 &&
        (latestMessage as any).ui_events[0].component === 'jira_issue_card' &&
        !!(latestMessage as any).ui_events[0].html;
      if (hasUiEvents) {
        isCard = true;
        logger.log('[Card Detection Effect] Jira RAG card detected via ui_events:', (latestMessage as any).ui_events);
      } else {
        // Fallback: array/object check for other modes
        const isArray = Array.isArray(latestMessage.content);
        isCard = isArray;
        logger.log('[Card Detection Effect] assistant content:', latestMessage.content);
        logger.log('[Card Detection Effect] isArray:', isArray, 'isCard:', isCard);
      }
      setLatestAssistantWasCard(isCard);
    }
    // Reset card state on new user message
    if (latestMessage && latestMessage.role === 'user') {
      logger.log('[Card Detection Effect] Reset latestAssistantWasCard to false');
      setLatestAssistantWasCard(false);
    }
  }, [displayChatHistory, chatModeState.ragChatType]);

    // Function to send message from textarea input through to the API call with full chat history
  const handleSendMessage = useCallback(async (message?: string, tools?: string[]) => {
    // Use the message parameter if provided (from ChatInput), otherwise use inputValue
    const messageToSend = message || inputValue.trim();
    
    if (!messageToSend || isStreaming) return;
    
    // Reset scroll behavior for new messages
    setShouldAutoScroll(true);
    setShowScrollToBottom(false);
    isUserScrollingRef.current = false;
    hasSeenNewContentRef.current = true; // Starting a new conversation, no unseen content yet
    // If user is initiating a new image generation prompt, hide usage alert immediately
    if (tools?.includes('image_generation')) {
      setShowImageUsageAlert(false);
    }
    
    // Only clear inputValue if we're using it (not when message is passed from ChatInput)
    if (!message) {
      setInputValue('');
    }
    
    await sendMessage(messageToSend, tools);
  }, [inputValue, isStreaming, sendMessage]);


  // --- SmartSuggestion logic ---
  // Only show in Jira RAG mode, if latest assistant message is not a card, and a valid PMO is found
  // Track if user ignored SmartSuggestion
  const [smartSuggestionDismissed, setSmartSuggestionDismissed] = React.useState(false);

  // Reset dismissal when a new assistant message arrives
  React.useEffect(() => {
    const latestMessage = displayChatHistory[displayChatHistory.length - 1];
    if (latestMessage && latestMessage.role === 'assistant') {
      setSmartSuggestionDismissed(false);
    }
  }, [displayChatHistory]);

  // Dismiss SmartSuggestion if user sends a manual message
  React.useEffect(() => {
    const latestMessage = displayChatHistory[displayChatHistory.length - 1];
    const prevMessage = displayChatHistory[displayChatHistory.length - 2];
    if (
      latestMessage && latestMessage.role === 'user' &&
      (!prevMessage || prevMessage.role !== 'user')
    ) {
      setSmartSuggestionDismissed(true);
    }
  }, [displayChatHistory]);

  const showSmartSuggestion = React.useMemo(() => {
    if (chatModeState.ragChatType !== 'JIRA') return false;
    if (smartSuggestionDismissed) return false;
    const latestMessage = displayChatHistory[displayChatHistory.length - 1];
    if (!latestMessage || latestMessage.role !== 'assistant') return false;
    // If the latest assistant message was a card, do not show
    if (latestAssistantWasCard) return false;
    // If any message in the entire chat history is a Jira card, do not show
    const hasCardInHistory = displayChatHistory.some((msg: any) =>
      msg.role === 'assistant' &&
      msg.ui_events && msg.ui_events.length > 0 && msg.ui_events[0].component === 'jira_issue_card' && !!msg.ui_events[0].html
    );
    if (hasCardInHistory) return false;
    // Only if content is string and contains a valid PMO
    if (typeof latestMessage.content !== 'string') return false;
    const pmo = extractFirstValidPMO(latestMessage.content);
    return !!pmo;
  }, [chatModeState.ragChatType, displayChatHistory, latestAssistantWasCard, smartSuggestionDismissed]);

  const smartSuggestionPMO = React.useMemo(() => {
    if (!showSmartSuggestion) return null;
    const latestMessage = displayChatHistory[displayChatHistory.length - 1];
    if (!latestMessage || typeof latestMessage.content !== 'string') return null;
    return extractFirstValidPMO(latestMessage.content);
  }, [showSmartSuggestion, displayChatHistory]);



  // Handler for SmartSuggestion arrow click
  const handleSmartSuggestionSend = React.useCallback((pmo: string) => {
    if (!pmo) return;
    // Send as user message
    handleSendMessage(pmo);
  }, [handleSendMessage]);

  // --- Scroll-to-bottom ONCE per new user message ---
  const hasAutoScrolledForLatestUserMessage = React.useRef(false);
  useEffect(() => {
    const latestMessage = displayChatHistory[displayChatHistory.length - 1];
    const prevMessage = displayChatHistory[displayChatHistory.length - 2];
    // Reset flag if new user message
    if (latestMessage && latestMessage.role === 'user' && (!prevMessage || prevMessage.role !== 'user')) {
      hasAutoScrolledForLatestUserMessage.current = false;
    }
    // Only scroll ONCE for new user message, and only if not scrolling up
    if (
      latestMessage && latestMessage.role === 'user' &&
      !hasAutoScrolledForLatestUserMessage.current &&
      !isUserScrollingRef.current
    ) {
      setTimeout(() => {
        if (scrollContainerRef.current) {
          const { scrollHeight, clientHeight } = scrollContainerRef.current;
          if (scrollHeight > clientHeight) {
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
          }
        }
      }, 100);
      setShouldAutoScroll(true);
      isUserScrollingRef.current = false;
      hasAutoScrolledForLatestUserMessage.current = true;
    }
  }, [displayChatHistory, shouldAutoScroll, chatModeState.ragChatType, latestAssistantWasCard]);

  // Dedicated effect: Scroll to bottom for Jira RAG assistant messages ONLY after confirming not a card
  useEffect(() => {
    if (chatModeState.ragChatType !== 'JIRA') return;
    const latestMessage = displayChatHistory[displayChatHistory.length - 1];
    // If assistant message is a card, scroll so card top is visible, offset by 255px
    if (
      latestMessage &&
      latestMessage.role === 'assistant' &&
      (
        Array.isArray(latestMessage.content) ||
        (typeof latestMessage === 'object' && latestMessage !== null && 'ui_events' in latestMessage && Array.isArray(latestMessage.ui_events) && latestMessage.ui_events.length > 0 && (latestMessage as any).ui_events[0].component === 'jira_issue_card' && !!(latestMessage as any).ui_events[0].html)
      )
    ) {
      setTimeout(() => {
        if (scrollContainerRef.current) {
          // Find the card element
          const lastIndex = displayChatHistory.length - 1;
          const cardEl = scrollContainerRef.current.querySelector(`[data-chat-index="${lastIndex}"]`);
          if (cardEl) {
            const cardElem = cardEl as HTMLElement;
            const cardTop = cardElem.offsetTop;
            const cardHeight = cardElem.offsetHeight;
            const offset = Math.floor(cardHeight * 0.25);
            scrollContainerRef.current.scrollTop = Math.max(cardTop - offset, 0);
          }
        }
      }, 120);
      setShouldAutoScroll(true);
      isUserScrollingRef.current = false;
      return;
    }
    // Only scroll for assistant messages, and only if not a card
    if (
      latestMessage &&
      latestMessage.role === 'assistant' &&
      latestAssistantWasCard === false &&
      !(
        Array.isArray(latestMessage.content) ||
        (typeof latestMessage === 'object' && latestMessage !== null && 'ui_events' in latestMessage && Array.isArray(latestMessage.ui_events) && latestMessage.ui_events.length > 0 && (latestMessage as any).ui_events[0].component === 'jira_issue_card' && !!(latestMessage as any).ui_events[0].html)
      )
    ) {
      setTimeout(() => {
        if (scrollContainerRef.current) {
          const { scrollHeight, clientHeight } = scrollContainerRef.current;
          if (scrollHeight > clientHeight) {
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
          }
        }
      }, 120);
      setShouldAutoScroll(true);
      isUserScrollingRef.current = false;
    }
  }, [displayChatHistory, latestAssistantWasCard, chatModeState.ragChatType]);

  // Effect: Scroll to bottom for assistant messages only after latestAssistantWasCard is settled

  // For Jira RAG, always suppress scroll-to-bottom for assistant messages. No scroll logic here.
  // (Scroll-to-bottom for Jira RAG will be handled separately later if needed.)

  // Mark content as unseen when streaming starts or new content arrives
  // But only if user has manually scrolled up
  useEffect(() => {
    // Calculate isWaitingForResponse here to ensure it's always up to date
    const latestMessage = displayChatHistory[displayChatHistory.length - 1];
    const isWaitingForResponse = latestMessage && latestMessage.role === 'user' && !isStreaming;
    // If streaming starts and user is already scrolled up, show the arrow immediately
    if (isStreaming && currentResponse && isUserScrollingRef.current) {
      hasSeenNewContentRef.current = false;
      setShowScrollToBottom(true);
    }
    // If waiting for response and user is already scrolled up, show the arrow
    if (isWaitingForResponse && isUserScrollingRef.current) {
      hasSeenNewContentRef.current = false;
      setShowScrollToBottom(true);
    }
  }, [isStreaming, currentResponse, displayChatHistory]);

  // Attach scroll event listener
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Function to copy message to clipboard
  const handleCopyMessage = async (messageContent: string, isAssistant: boolean = false, messageIndex?: number, chartImages?: string[]) => {
    try {
      // Check if the message contains a generated image
      const imageMatch = messageContent.match(/!\[Generated Image\]\(data:image\/png;base64,([A-Za-z0-9+/=]+)\)/);
      
      if (imageMatch) {
        // Copy the image to clipboard
        const base64Data = imageMatch[1];
        
        // Convert base64 to blob
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/png' });
        
        // Copy image to clipboard
        await navigator.clipboard.write([
          new ClipboardItem({
            'image/png': blob
          })
        ]);
        
        if (isAssistant && messageIndex !== undefined) {
          // Assistant message - set permanent checkmark
          setCopiedAssistantMessages(prev => new Set(prev).add(messageIndex));
          // Clear checkmark after 1 second
          setTimeout(() => {
            setCopiedAssistantMessages(prev => {
              const newSet = new Set(prev);
              newSet.delete(messageIndex);
              return newSet;
            });
          }, 1000);
        } else {
          // User message - set temporary checkmark
          setJustCopied(messageContent);
          // Clear checkmark after 1 second
          setTimeout(() => setJustCopied(null), 1000);
        }
        return;
      }
      
      // Sanitize message prior to copying: strip large base64 image payloads & analysisfile encoded IDs
      // Replace inline generated image markdown with a short placeholder.
      let sanitized = messageContent
        .replace(/!\[Generated Image\]\(data:image\/png;base64,[A-Za-z0-9+/=]+\)/g, '[Generated Image Available — use Download button]')
        // Extract just the filename from analysisfile links for clean copying
        .replace(/\[Download\s+([^\]]+)\]\(analysisfile:[^:]+:[^:]+:([^)]+)\)/g, (match, label, encodedFileName) => {
          try {
            const fileName = decodeURIComponent(encodedFileName);
            return `File: ${fileName}`;
          } catch {
            return `File: ${label}`;
          }
        })
        .replace(/\[__REPLACE__\]/g,'');
      
      // Strip any thinking content (should already be removed, but defense in depth)
      sanitized = sanitized.replace(/<thinking>[\s\S]*?<\/thinking>/gi, '').replace(/<\/?thinking[^>]*>/gi, '');
      
      // Strip HTML img tags from sources (e.g., favicon images)
      // Replace: <img src="..." ... /> [domain](url) with just: [domain](url)
      sanitized = sanitized.replace(/<img[^>]*>\s*/gi, '');
      
      // Convert markdown to HTML (order matters!)
      
      // 1. Code blocks first (to protect their content from other conversions): ```language\ncode\n``` -> <pre><code>code</code></pre>
      sanitized = sanitized.replace(/```[\w]*\n([\s\S]*?)```/g, '<pre><code>$1</code></pre>');
      
      // 2. Convert markdown tables to HTML tables
      sanitized = convertMarkdownTablesToHTML(sanitized);
      
      // 3. Headers: ## Header -> <h2>Header</h2>
      sanitized = sanitized.replace(/^#### (.+)$/gm, '<h4>$1</h4>');
      sanitized = sanitized.replace(/^### (.+)$/gm, '<h3>$1</h3>');
      sanitized = sanitized.replace(/^## (.+)$/gm, '<h2>$1</h2>');
      sanitized = sanitized.replace(/^# (.+)$/gm, '<h1>$1</h1>');
      
      // 4. Horizontal rules: --- is kept as-is (no conversion to <hr>)

      // 5. Bold: **text** or __text__ -> <strong>text</strong> (BEFORE lists to handle bold inside lists)
      sanitized = sanitized.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      sanitized = sanitized.replace(/__(.+?)__/g, '<strong>$1</strong>');
      
      // 6. Italic: *text* or _text_ -> <em>text</em>
      sanitized = sanitized.replace(/\*(.+?)\*/g, '<em>$1</em>');
      sanitized = sanitized.replace(/_([^_]+?)_/g, '<em>$1</em>');
      
      // 7. Inline code: `code` -> <code>code</code>
      sanitized = sanitized.replace(/`([^`]+)`/g, '<code>$1</code>');
      
      // 8. Links: [text](url) -> <a href="url">text</a>
      sanitized = sanitized.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
      
      // 9. Lists - convert to <li> tags
      // Unordered lists: - item or * item
      sanitized = sanitized.replace(/^[*-] (.+)$/gm, '<li>$1</li>');
      // Ordered lists: 1. item  
      sanitized = sanitized.replace(/^\d+\. (.+)$/gm, '<liOL>$1</liOL>');
      
      // 10. Paragraphs: Convert double newlines to paragraph breaks
      sanitized = sanitized.replace(/\n\n+/g, '</p><p>');
      // Single newlines to <br>
      sanitized = sanitized.replace(/\n/g, '<br>');
      sanitized = '<p>' + sanitized + '</p>';
      
      // 11. Now wrap consecutive <li> tags with <ul> and consecutive <liOL> tags with <ol>
      // Use a more specific approach: find sequences of list items separated only by <br>
      // Unordered lists
      sanitized = sanitized.replace(/(<li>[^]*?<\/li>(<br>)?)+/g, (match) => {
        const cleaned = match.replace(/<br>/g, '');
        return `<ul>${cleaned}</ul>`;
      });
      // Ordered lists
      sanitized = sanitized.replace(/(<liOL>[^]*?<\/liOL>(<br>)?)+/g, (match) => {
        const cleaned = match.replace(/<br>/g, '');
        const withLi = cleaned.replace(/<liOL>/g, '<li>').replace(/<\/liOL>/g, '</li>');
        return `<ol>${withLi}</ol>`;
      });
      
      // 12. Clean up
      sanitized = sanitized.replace(/<p><\/p>/g, '');
      sanitized = sanitized.replace(/<p>\s*<\/p>/g, '');
      sanitized = sanitized.replace(/<\/ul>\s*<ul>/g, '');
      sanitized = sanitized.replace(/<\/ol>\s*<ol>/g, '');
      sanitized = sanitized.replace(/<p>(<table[\s\S]*?<\/table>)<\/p>/g, '$1');
      sanitized = sanitized.replace(/<p>(<h[1-6]>[\s\S]*?<\/h[1-6]>)<\/p>/g, '$1');
      sanitized = sanitized.replace(/<p>(<ul>[\s\S]*?<\/ul>)<\/p>/g, '$1');
      sanitized = sanitized.replace(/<p>(<ol>[\s\S]*?<\/ol>)<\/p>/g, '$1');
      sanitized = sanitized.replace(/<p>(<pre>[\s\S]*?<\/pre>)<\/p>/g, '$1');
      
      // Wrap HTML with proper styling for better paste experience
      const chartImgTags = (chartImages && chartImages.length > 0)
        ? chartImages.map(src => `<img src="${src}" style="max-width:100%;display:block;margin:12px 0;" alt="Chart" />`).join('')
        : '';
      const styledHTML = `
        <div style="font-family: Arial, sans-serif; font-size: 11pt; line-height: 1.6;">
          ${sanitized}
          ${chartImgTags}
        </div>
      `;
      
      // Create plain text version by stripping HTML tags.
      // Semantic closing tags are converted to whitespace first, then ALL '<' and '>'
      // are stripped as individual single characters. Replacing single characters
      // (rather than multi-character tag patterns) avoids the CodeQL
      // "incomplete multi-character sanitization" warning: there is no way for a
      // nested/malformed payload like "<scr<script>ipt>" to survive when every '<'
      // and '>' is unconditionally removed one character at a time.
      let plainText = sanitized
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n\n')
        .replace(/<p>/gi, '')
        .replace(/<\/h[1-6]>/gi, '\n\n')
        .replace(/<h[1-6]>/gi, '')
        .replace(/<\/li>/gi, '\n')
        .replace(/<li>/gi, '')
        .replace(/<\/tr>/gi, '\n')
        .replace(/<tr>/gi, '')
        .replace(/<\/td>/gi, '\t')
        .replace(/<td>/gi, '')
        .replace(/<\/ul>/gi, '\n')
        .replace(/<ul>/gi, '')
        .replace(/<\/ol>/gi, '\n')
        .replace(/<ol>/gi, '')
        .replace(/<\/pre>/gi, '\n')
        .replace(/<pre>/gi, '')
        .replace(/<\/code>/gi, '')
        .replace(/<code>/gi, '')
        .replace(/<\/strong>/gi, '')
        .replace(/<strong>/gi, '')
        .replace(/<\/em>/gi, '')
        .replace(/<em>/gi, '')
        .replace(/<\/a>/gi, '')
        .replace(/<\/table>/gi, '\n')
        .replace(/<table>/gi, '')
        .replace(/<\/thead>/gi, '')
        .replace(/<thead>/gi, '')
        .replace(/<\/tbody>/gi, '')
        .replace(/<tbody>/gi, '')
        .replace(/<\/th>/gi, '\t')
        .replace(/<th>/gi, '')
        .replace(/<\/tfoot>/gi, '')
        .replace(/<tfoot>/gi, '')
        .replace(/<\/div>/gi, '')
        .replace(/<div>/gi, '')
        .replace(/<\/span>/gi, '')
        .replace(/<span>/gi, '')
        .replace(/</g, '')             // strip every '<' individually (catches attributes & any remaining tags)
        .replace(/>/g, '')             // strip every '>' individually
        .replace(/\n{3,}/g, '\n\n')
        .trim();
      
      // Use ClipboardItem API to copy both HTML and plain text
      const htmlBlob = new Blob([styledHTML], { type: 'text/html' });
      const textBlob = new Blob([plainText], { type: 'text/plain' });
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': htmlBlob,
          'text/plain': textBlob
        })
      ]);
      
      if (isAssistant && messageIndex !== undefined) {
        // Assistant message - set permanent checkmark
        setCopiedAssistantMessages(prev => new Set(prev).add(messageIndex));
        // Clear checkmark after 1 second
        setTimeout(() => {
          setCopiedAssistantMessages(prev => {
            const newSet = new Set(prev);
            newSet.delete(messageIndex);
            return newSet;
          });
        }, 1000);
      } else {
        // User message - set temporary checkmark
        setJustCopied(messageContent);
        // Clear checkmark after 1 second
        setTimeout(() => setJustCopied(null), 1000);
      }
    } catch (err) {
      console.error('Failed to copy message:', err);
    }
  };

  // Function to download image from message content
  const handleDownloadImage = (messageContent: string) => {
    // Check if the message content contains a generated image
    const imageMatch = messageContent.match(/!\[Generated Image\]\(data:image\/png;base64,([A-Za-z0-9+/=]+)\)/);
    
    if (imageMatch) {
      const base64Data = imageMatch[1];
      
      // Create download link
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${base64Data}`;
      link.download = `generated-image-${Date.now()}.png`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Function to download JIRA report in specified format
  const handleDownloadJiraReport = (format: 'pdf' | 'docx', messageIndex: number, pdfTheme: 'light' | 'dark' = 'light') => {
    logger.log(`[ChatPage] Downloading JIRA report as ${format} (theme: ${pdfTheme}) for message ${messageIndex}`);

    // Close overlays immediately
    setShowDownloadOverlay(null);
    setShowJiraFormatOverlay(false);

    try {
      const message = displayChatHistory[messageIndex];
      if (!message || message.role !== 'assistant') {
        logger.warn('[ChatPage] Invalid message for download');
        return;
      }

      const filename = `jira-report-${Date.now()}`;
      const msgAny = message as any;
      const hasCard = msgAny.ui_events && msgAny.ui_events.length > 0 && msgAny.ui_events[0].html;

      if (format === 'pdf' && hasCard) {
        // Build the PDF entirely from the stored HTML string so the live card
        // on screen is never touched and its styling never flashes.
        printDomElementAsPDF(msgAny.ui_events[0].html, filename, pdfTheme).catch((err) => {
          console.error('Failed to capture card as PDF:', err);
        });
        return;
      }

      // DOC export (or PDF fallback): use HTML reconstruction
      if (typeof message.content === 'string') {
        let contentToDownload = message.content;
        if (hasCard && msgAny.ui_events[0].component === 'jira_issue_card') {
          contentToDownload = msgAny.ui_events[0].html;
        }
        downloadMessageAsDocument(contentToDownload, format, filename, 'JIRA Report');
      }
    } catch (err) {
      console.error('Failed to download JIRA report:', err);
    }
  };

  // Map from message index -> captured chart PNG dataUrls, populated when PlotlyChartViewer renders
  const chartImagesMapRef = React.useRef<Map<number, string[]>>(new Map());

  // Function to download entire chat history as Word document
  const handleDownloadChatHistory = () => {
    try {
      downloadChatHistory(displayChatHistory, undefined, chartImagesMapRef.current);
      // Close overlays if open
      setShowDownloadOverlay(null);
      setShowJiraFormatOverlay(false);
    } catch (err) {
      console.error('Failed to download chat history:', err);
    }
  };

  // Function to check if message contains an image
  const messageContainsImage = (messageContent: string | ChatContentItem[]) => {
    if (typeof messageContent === 'string') {
      return messageContent.includes('![Generated Image](data:image/png;base64,');
    }
    // For structured content, scan any input_text items
    return messageContent.some(item => item.type === 'input_text' && item.text?.includes('![Generated Image](data:image/png;base64,'));
  };

  // Handle ChatInput height changes
  const handleChatInputHeightChange = React.useCallback((height: number) => {
    // Add offset for spacing between content and ChatInput
    setChatInputHeight(height + 60);
  }, []);
  
  // Force scroll container padding update when voice mode changes
  React.useLayoutEffect(() => {
    // Update padding immediately (before paint)
    // Voice mode: component is ~240px tall but positioned 48px from bottom = 288px + 12px buffer = 300px
    if (scrollContainerRef.current) {
      const currentPadding = isVoiceModeActive ? '300px' : `${chatInputHeight}px`;
      scrollContainerRef.current.style.paddingBottom = currentPadding;
      
      // Force layout recalculation before scrolling to ensure padding is applied
      void scrollContainerRef.current.offsetHeight;
      
      // Scroll to bottom after padding update to align content properly
      // This ensures content aligns correctly both entering AND exiting voice mode
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
      });
    }
  }, [isVoiceModeActive, chatInputHeight]);
  
  // Separate effect to handle scroll position when exiting voice mode
  // This ensures scroll updates even when chatInputHeight hasn't changed
  useLayoutEffect(() => {
    if (!isVoiceModeActive && scrollContainerRef.current) {
      // Small delay to ensure padding update from previous effect has been applied
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
          }
        });
      });
    }
  }, [isVoiceModeActive]);
  
  // Auto-scroll when voice history changes (new messages in voice mode)
  useLayoutEffect(() => {
    if (isVoiceModeActive && voiceHistory.length > 0 && shouldAutoScroll && scrollContainerRef.current) {
      // Scroll to bottom when new voice messages arrive
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    }
  }, [isVoiceModeActive, voiceHistory.length, shouldAutoScroll, scrollToBottom]);

  // Helper method to tidy up Jira logs
  // const processJiraLogs = useCallback((logs: string[]) => {
  //     return logs.map((log: string) => {
  //       let out = log.replace(/\\n/g, '\n');   
  //       out = out.replace(/\[?\{'type':\s*'text',\s*'text':\s*'((?:[^'\\]|\\.)*)'\}?\]?/g, '$1');    
  //       out = out.replace(/^(\[[^\]]+\])\*+\s*/, '$1 ');
        
  //       return out.split(' | ').join('\n\n');
  //     }).join('\n\n---\n\n');
  // }, []);

  return (
    <main 
      className="flex-1 w-0 flex flex-col bg-[var(--main-background)] relative h-full" 
    >
      {/* Scrollable content area */}
      <div 
        ref={scrollContainerRef}
        // Removed fixed left-56/right padding; use responsive horizontal padding so margins shrink first.
        className="absolute inset-0 overflow-y-auto chat-page-scroll px-4 sm:px-6 md:px-8 lg:px-12 xl:px-20 2xl:px-28"
        style={{ 
          overflowX: 'hidden',
        }}
      >
        {/* Chat conversation area */}
        <div ref={messagesContainerRef} className="w-full pt-8 max-w-4xl mx-auto" style={{ overflowX: 'clip', overflowY: 'visible' }}>
        
        {/* Network warning - show above first message when slow connection detected */}
        {showNetworkWarning && displayChatHistory.length > 0 && (
          <NetworkWarning 
            onDismiss={() => {
              setShowNetworkWarning(false);
              setNetworkWarningDismissed(true);
              try {
                sessionStorage.setItem('networkWarningDismissed', 'true');
              } catch {
                // Ignore sessionStorage errors
              }
            }}
          />
        )}
        
        {/* Chat messages - all messages now appear here immediately */}
        {displayChatHistory.map((message: any, index: number) => {
          return (
            <motion.div
              key={message.id || index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-6"
              data-chat-index={index}
            >
            {message.role === 'user' ? (
              <div className="flex justify-end items-start gap-3 mb-4">
                <div className="relative group">
                  <div 
                    className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      mode === 'dark' 
                        ? 'bg-[var(--color-secondary)] text-[var(--color-text)]'
                        : 'bg-gray-200 text-[var(--color-text)]'
                    }`}
                    style={{ wordWrap: 'break-word' }}
                  >
                    {/* Voice message with transcribing state */}
                    {message.isVoice && message.isTranscribing ? (
                      <div className="flex items-center gap-2 text-sm">
                        <div className="animate-pulse flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-[var(--color-accent)] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-1.5 h-1.5 bg-[var(--color-accent)] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-1.5 h-1.5 bg-[var(--color-accent)] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </div>
                        <span className="text-[var(--color-text)] opacity-70">Transcribing...</span>
                      </div>
                    ) : Array.isArray(message.content) ? (
                      <div className="text-sm space-y-2">
                        {message.content.map((item: ChatContentItem, i: number) => {
                          if (item.type === 'input_image') {
                            // Handle input_image type (images uploaded by user)
                            return (
                              <div key={i} className="inline-block">
                                <img
                                  src={item.image_url}
                                  alt="Uploaded file"
                                  className="w-20 h-20 object-cover rounded-md border border-[var(--color-accent)]"
                                />
                              </div>
                            );
                          }
                          if (item.type === 'input_file') {
                            const isImg = item.file_data?.startsWith('data:image');
                            return (
                              <div key={i} className="inline-block">
                                {isImg ? (
                                  <img
                                    src={item.file_data}
                                    alt={item.filename}
                                    className="w-20 h-20 object-cover rounded-md border border-[var(--color-accent)]"
                                  />
                                ) : (
                                  <span className="px-2 py-1 rounded bg-[var(--color-secondary)] border border-[var(--color-accent)] text-xs font-medium inline-flex items-center">
                                    {item.filename}
                                  </span>
                                )}
                              </div>
                            );
                          }
                          if (item.type === 'input_text') {
                            return <p key={i} className="whitespace-pre-line">{item.text}</p>;
                          }
                          return null;
                        })}
                      </div>
                    ) : (
                      <p className="text-sm whitespace-pre-line" style={message.isVoice ? { fontStyle: 'italic' } : undefined}>
                        {message.isVoice 
                          ? message.content === null
                            ? 'Transcription unavailable'
                            : `"${message.content}"`
                          : message.content
                        }
                      </p>
                    )}
                  </div>
                  
                  {/* Copy button - appears below message on hover */}
                  <div className="flex justify-end opacity-0 group-hover:opacity-70 transition-all duration-200 transform translate-y-1 group-hover:translate-y-0 mt-1">
                    <button
                      onClick={async () => {
                        if (message.content === null) {
                          await handleCopyMessage('Transcription unavailable');
                          setJustCopiedByIndex(index);
                          setTimeout(() => setJustCopiedByIndex(null), 1000);
                        } else if (typeof message.content === 'string') {
                          handleCopyMessage(message.content);
                        } else {
                          handleCopyMessage(message.content.map((ci: any) => {
                            if (ci.type === 'input_text') return ci.text || '';
                            if (ci.type === 'input_file') return `[file:${ci.filename}]`;
                            if (ci.type === 'input_image') return '[image]';
                            return '';
                          }).join('\n'));
                        }
                      }}
                      className={`p-1 text-[var(--color-text)] transition-colors duration-200 opacity-100 hover:opacity-70`}
                      title="Copy message"
                    >
                      {(message.content !== null ? justCopied === message.content : justCopiedByIndex === index) ? (
                        <CheckIcon className="w-4 h-4" />
                      ) : (
                        <CopyIcon className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
                
                {/* User initials circle with hover edit overlay */}
                <ThemeDialogTrigger>
                  <div 
                    className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center relative group cursor-pointer -mt-0.5"
                    style={{
                      background: userBubbleColor === 'default' 
                        ? (mode === 'dark' ? 'var(--color-secondary)' : '#e5e7eb') 
                        : userBubbleColor,
                      border: userBubbleColor === 'default' && mode === 'dark' 
                        ? '1px solid var(--color-border)' 
                        : 'none'
                    }}
                    title="Click to customize your bubble color"
                  >
                    <span
                      className="text-sm font-semibold"
                      style={{ color: computeBubbleTextColor(userBubbleColor) }}
                    >{getUserInitials(account?.name)}</span>
                    
                    {/* Hover overlay with pencil icon */}
                    <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <Pencil1Icon className="w-4 h-4 text-white" strokeWidth={2} />
                    </div>
                  </div>
                </ThemeDialogTrigger>
              </div>
            ) : message.error ? (
              // ERROR MESSAGE STYLING
              <div className="flex justify-start items-start gap-3 mb-6">
                 {/* Nav avatar with error/warning state */}
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-red-100 border border-red-300`}>
                  <span className={`text-sm font-semibold text-red-600`}>!</span>
                </div>
                <div className={`flex-1 px-3 py-2 rounded-lg bg-red-100 border border-red-300`}>
                  <div className="flex items-center justify-between gap-3">
                    <p className={`text-sm flex-1 m-0 text-red-700`}>
                      <strong>Error:</strong> {message.content}
                      {message.content.includes('Translation failed') && (
                            <> &ndash; <span
                              className="text-blue-600 cursor-pointer hover:underline"
                              onClick={() => window.open('https://skydrive3m-my.sharepoint.com/:w:/g/personal/aasn6zz_mmm_com/IQCjavUkh3HVSaxlOtozWQRGAaWLaQYkpK833eSKhO8vDb8?e=rO9AfG', '_blank')}
                            >Click for Guide to Glossary</span></>
                      )}
                    </p>
                  </div>
                </div>
              </div>
             ) : (
              <div className="flex justify-start items-start gap-3">
                {/* Nav avatar */}
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                  mode === 'dark' 
                    ? 'bg-transparent border-2 border-[var(--color-accent)]' 
                    : 'bg-[var(--color-secondary)] border border-[var(--color-border)]'
                }`}
                style={userBubbleColor === 'default' ? {
                  background: '#D10411',
                  borderColor: mode === 'dark' ? 'var(--color-accent)' : 'var(--color-border)'
                } : userBubbleColor !== 'default' ? {
                  background: mode === 'dark' ? 'var(--color-secondary)' : '#e5e7eb',
                  borderColor: mode === 'dark' ? 'var(--color-accent)' : 'var(--color-border)'
                } : {}}>
                  <span
                    className="text-sm font-semibold"
                    style={{ color: userBubbleColor === 'default' ? '#ffffff' : (mode === 'dark' ? '#ffffff' : 'var(--color-text)') }}
                  >Nav</span>
                </div>
                <div className={`flex-1 rounded-lg ${isMobile ? 'px-3 py-2' : 'px-8 py-6'} ${
                  mode === 'dark'
                    ? 'bg-transparent border-2 border-[var(--color-accent)]'
                    : 'bg-[var(--color-secondary)] border border-[var(--color-border)]'
                }`} style={{ minWidth: 0, contain: 'layout style' }}>
                  <div className="text-sm text-[var(--color-text)]">
                    {/* Voice message indicator */}
                    {message.isVoice && message.content && (
                      <div className="flex items-center gap-2 mb-2 text-xs opacity-70">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                        </svg>
                        <span style={{paddingTop: '0.25rem'}}>Voice response</span>
                      </div>
                    )}
                    {message.reasoning && (
                      <ReasoningDropdown reasoning={message.reasoning} dark={mode === 'dark'} />
                    )}
                    {(() => {
                      if (message.thinking) {
                        logger.log('[ChatPage][rendering-thinking-dropdown]', { 
                          thinkingLength: message.thinking.length,
                          thinkingPreview: message.thinking.slice(0, 100)
                        });
                        return <ReasoningDropdown reasoning={message.thinking} dark={mode === 'dark'} title="Thinking" />;
                      } else {
                        return null;
                      }
                    })()}
                    
                    {/* JIRA logs dropdown - display logs from non-streaming JIRA responses */}
                    {/* {message.logs && Array.isArray(message.logs) && message.logs.length > 0 && (
                      <ReasoningDropdown 
                        reasoning={processJiraLogs(message.logs)} 
                        dark={mode === 'dark'} 
                        title="Logs" 
                      />
                    )}  */}
                    
                    {/* Function call status (for active function calls) */}
                    {message.functionCall && (
                      <FunctionCallStatus
                        type={message.functionCall.type}
                        message={message.functionCall.message}
                        isActive={message.functionCall.isActive || false}
                      />
                    )}
                    
                    {/* Render Jira markdown if in Jira mode, otherwise use standard renderer */}
                    {chatModeState.ragChatType === 'JIRA' && typeof message.content === 'string' ? (
                      <JiraMarkdown 
                        content={message.content} 
                        ui_events={(message as any).ui_events}
                        onChartImagesReady={(images) => { chartImagesMapRef.current.set(index, images); }}
                      />
                    ) : (
                      <MessageContentRenderer 
                        content={message.content} 
                        debugInfo={debugInfo}
                        onContentLoaded={() => handleMessageContentLoaded(index)}
                        translationData={(message as any).translationData}
                      />
                    )}
                    
                    {/* Sources (for completed function calls with results and RAG citations) */}
                    {message.sources && message.sources.length > 0 && (
                      <SourcesList 
                        sources={message.sources}
                        hideIcons={isRestRagMode || isWebSocketRagMode}
                        hideUrlDetails={isRestRagMode || isWebSocketRagMode}
                      />
                    )}
                  </div>
                  
                  {/* Action buttons - always visible for completed assistant messages */}
                  {translationHistory.length === 0 && (<div className="mt-4 flex gap-2 justify-between">
                    <div className="flex gap-2">
                      {/* Copy button */}
                      <button
                        onClick={async () => {
                          const cardHtml = (message as any).ui_events?.[0]?.html;
                          if (cardHtml) {
                            // Jira card: copy as Word-compatible HTML + plain text
                            try {
                              const wordHtml = createJiraCardDocument(cardHtml);
                              const tmp = document.createElement('div');
                              tmp.innerHTML = wordHtml;
                              const plainText = tmp.innerText || tmp.textContent || '';
                              await navigator.clipboard.write([
                                new ClipboardItem({
                                  'text/html': new Blob([wordHtml], { type: 'text/html' }),
                                  'text/plain': new Blob([plainText], { type: 'text/plain' }),
                                }),
                              ]);
                              setCopiedAssistantMessages(prev => new Set(prev).add(index));
                              setTimeout(() => setCopiedAssistantMessages(prev => { const s = new Set(prev); s.delete(index); return s; }), 1000);
                            } catch (err) {
                              console.error('Failed to copy Jira card:', err);
                            }
                            return;
                          }
                          typeof message.content === 'string'
                            ? handleCopyMessage(message.content, true, index, chartImagesMapRef.current.get(index))
                            : handleCopyMessage(message.content.map((ci: any) => {
                                if (ci.type === 'input_text') return ci.text || '';
                                if (ci.type === 'input_file') return `[file:${ci.filename}]`;
                                if (ci.type === 'input_image') return '[image]';
                                return '';
                              }).join('\n'), true, index, chartImagesMapRef.current.get(index));
                        }}
                        className={`w-8 h-8 flex items-center justify-center rounded-md leading-none transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--color-accent)] text-[var(--color-text)] hover:opacity-70`}
                        title="Copy message"
                      >
                        {copiedAssistantMessages.has(index) ? (
                          <CheckIcon className="w-4 h-4" />
                        ) : (
                          <CopyIconWrapper size={16} />
                        )}
                      </button>
                      
                      {/* Download button with conditional overlays */}
                      {(() => {
                        const isJiraSelected = chatModeState.ragChatType === 'JIRA';
                        const isLastMessage = index === displayChatHistory.length - 1;
                        
                        // Case 1: JIRA not selected - show simple download on last message only
                        if (!isJiraSelected && isLastMessage) {
                          return (
                            <button
                              onClick={handleDownloadChatHistory}
                              className={`w-8 h-8 flex items-center justify-center rounded-md leading-none transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--color-accent)] text-[var(--color-text)] hover:opacity-70`}
                              title="Download chat history"
                            >
                              <DownloadIcon className="w-4 h-4" />
                            </button>
                          );
                        }
                        
                        // Case 2: JIRA selected, last message, no card - simple chat history download
                        if (isJiraSelected && isLastMessage && !latestAssistantWasCard) {
                          return (
                            <button
                              onClick={handleDownloadChatHistory}
                              className={`w-8 h-8 flex items-center justify-center rounded-md leading-none transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--color-accent)] text-[var(--color-text)] hover:opacity-70`}
                              title="Download chat history"
                            >
                              <DownloadIcon className="w-4 h-4" />
                            </button>
                          );
                        }

                        // Case 3 & 4: JIRA selected with card - show download button with overlays on click
                        // For non-last messages, only show the button if this specific message is a Jira card
                        const msgAnyCheck = message as any;
                        const thisMessageIsCard = msgAnyCheck.ui_events && msgAnyCheck.ui_events.length > 0 && !!msgAnyCheck.ui_events[0].html;
                        if (isJiraSelected && (!isLastMessage && !thisMessageIsCard)) {
                          return null;
                        }
                        if (isJiraSelected) {
                          return (
                            <div 
                              ref={showDownloadOverlay === index ? downloadOverlayRef : undefined}
                              className="relative"
                              onMouseEnter={(e) => {
                                if (hideOverlayTimeoutRef.current) clearTimeout(hideOverlayTimeoutRef.current);
                                // Use currentTarget to find the button inside
                                const btn = e.currentTarget.querySelector('button');
                                if (btn) setDownloadButtonRect(btn.getBoundingClientRect());
                                setShowDownloadOverlay(index);
                                setDownloadOverlayLocked(true);
                              }}
                            >
                              <button
                                className={`w-8 h-8 flex items-center justify-center rounded-md leading-none transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--color-accent)] text-[var(--color-text)] hover:opacity-70`}
                                title={isLastMessage ? "Download options" : "Export JIRA report"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (showDownloadOverlay === index) {
                                    setShowDownloadOverlay(null);
                                    setShowJiraFormatOverlay(false);
                                    setDownloadOverlayLocked(false);
                                  } else {
                                    setDownloadButtonRect(e.currentTarget.getBoundingClientRect());
                                    setShowDownloadOverlay(index);
                                    setShowJiraFormatOverlay(false);
                                    setDownloadOverlayLocked(true);
                                  }
                                }}
                              >
                                <DownloadIcon className="w-4 h-4" />
                              </button>

                              {/* Overlays — rendered via portal into document.body so they
                                  escape the scroll container's stacking context and always
                                  paint above the chat input bar (zIndex:100). */}
                              {showDownloadOverlay === index && downloadButtonRect && createPortal(
                                <div
                                  ref={portalDropdownRef}
                                  style={{
                                    position: 'fixed',
                                    // Anchor bottom of dropdown to top of button
                                    top: downloadButtonRect.top - 4,
                                    left: downloadButtonRect.left,
                                    transform: 'translateY(-100%)',
                                    zIndex: 25100,
                                  }}
                                  onMouseEnter={() => {
                                    if (hideOverlayTimeoutRef.current) clearTimeout(hideOverlayTimeoutRef.current);
                                    setDownloadOverlayLocked(true);
                                  }}
                                >
                                  {/* Main dropdown */}
                                  <div
                                    data-overlay="download"
                                    className="rounded-md shadow-xl overflow-visible"
                                    style={{
                                      backgroundColor: mode === 'dark' ? '#2a2a2d' : '#ffffff',
                                      border: `1px solid ${mode === 'dark' ? '#3a3a3d' : '#d1d5db'}`,
                                      minWidth: '160px',
                                    }}
                                  >
                                    {(() => {
                                      // Reusable format buttons renderer
                                      const renderFormatButtons = () => (
                                        <>
                                          <button
                                            onClick={() => handleDownloadJiraReport('pdf', index, 'light')}
                                            className="w-full flex items-center px-3 py-2.5 transition text-left"
                                            style={{ color: 'var(--color-text)' }}
                                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = mode === 'dark' ? '#6B7280' : '#f3f4f6'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                          >
                                            {React.createElement(FaRegFilePdf as any, { size: 14, className: "mr-2.5 flex-shrink-0" })}
                                            <span style={{ fontFamily: '3MCircularTT', fontSize: '12px', position: 'relative', top: '2px' }}>PDF (Light)</span>
                                          </button>

                                          <button
                                            onClick={() => handleDownloadJiraReport('pdf', index, 'dark')}
                                            className="w-full flex items-center px-3 py-2.5 transition text-left"
                                            style={{ color: 'var(--color-text)' }}
                                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = mode === 'dark' ? '#6B7280' : '#f3f4f6'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                          >
                                            {React.createElement(FaRegFilePdf as any, { size: 14, className: "mr-2.5 flex-shrink-0" })}
                                            <span style={{ fontFamily: '3MCircularTT', fontSize: '12px', position: 'relative', top: '2px' }}>PDF (Dark)</span>
                                          </button>
                                          
                                          <button
                                            onClick={() => handleDownloadJiraReport('docx', index)}
                                            className="w-full flex items-center px-3 py-2.5 transition text-left"
                                            style={{ color: 'var(--color-text)' }}
                                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = mode === 'dark' ? '#6B7280' : '#f3f4f6'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                          >
                                            {React.createElement(BsFiletypeDocx as any, { size: 14, className: "mr-2.5 flex-shrink-0" })}
                                            <span style={{ fontFamily: '3MCircularTT', fontSize: '12px', position: 'relative', top: '2px' }}>DOC</span>
                                          </button>
                                        </>
                                      );

                                      // Check if this specific message has a Jira card (ui_events with html)
                                      const msgAny = message as any;
                                      const messageIsCard = msgAny.ui_events && msgAny.ui_events.length > 0 && !!msgAny.ui_events[0].html;

                                      // Render based on message position
                                      if (isLastMessage) {
                                        return (
                                          <>
                                            <button
                                              onClick={handleDownloadChatHistory}
                                              className="w-full flex items-center px-3 py-2.5 transition text-left"
                                              style={{ color: 'var(--color-text)' }}
                                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = mode === 'dark' ? '#6B7280' : '#f3f4f6'; }}
                                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                            >
                                              <FileTextIcon width={16} height={16} className="mr-2.5 flex-shrink-0" />
                                              <span style={{ fontFamily: '3MCircularTT', fontSize: '12px', position: 'relative', top: '1px' }}>Chat History</span>
                                            </button>
                                            
                                            {latestAssistantWasCard && (
                                            <div 
                                              className="relative"
                                              onMouseEnter={() => setShowJiraFormatOverlay(true)}
                                            >
                                              <div
                                                className="w-full flex items-center justify-between px-3 py-2.5 transition cursor-pointer"
                                                style={{ color: 'var(--color-text)' }}
                                                onClick={(e) => { e.stopPropagation(); setShowJiraFormatOverlay(v => !v); }}
                                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = mode === 'dark' ? '#6B7280' : '#f3f4f6'; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                              >
                                                <div className="flex items-center">
                                                  {React.createElement(RiFolderDownloadLine as any, { size: 16, className: "mr-2.5 flex-shrink-0" })}
                                                  <span style={{ fontFamily: '3MCircularTT', fontSize: '12px', position: 'relative', top: '1px' }}>JIRA Report</span>
                                                </div>
                                                <ChevronDownIcon className="w-3.5 h-3.5 transform -rotate-90 flex-shrink-0 ml-2" />
                                              </div>
                                              
                                              {/* JIRA format sub-overlay — rendered inline here since the
                                                  parent portal container already has zIndex:25100 */}
                                              {showJiraFormatOverlay && (
                                                <div
                                                  data-overlay="jira-format"
                                                  className="absolute left-full top-0 rounded-md shadow-xl overflow-hidden"
                                                  style={{
                                                    backgroundColor: mode === 'dark' ? '#2a2a2d' : '#ffffff',
                                                    border: `1px solid ${mode === 'dark' ? '#3a3a3d' : '#d1d5db'}`,
                                                    minWidth: '120px',
                                                    marginLeft: '4px'
                                                  }}
                                                >
                                                  {renderFormatButtons()}
                                                </div>
                                              )}
                                            </div>
                                            )}
                                          </>
                                        );
                                      } else {
                                        // Not last message: Show format options directly (only if message has a card)
                                        if (messageIsCard) {
                                          return renderFormatButtons();
                                        }
                                        return null;
                                      }
                                    })()}
                                  </div>
                                </div>,
                                document.body
                              )}
                            </div>
                          );
                        }
                        
                        return null;
                      })()}
                      
                      {/* Retry button for stopped messages */}
                      { (message as any).isStopped && (
                        <button
                          onClick={() => {
                            try {
                              const failed = (message as any).failedUserMessage;
                              if (failed && typeof failed.content === 'string') {
                                // Remove the stopped message from history before retrying
                                if (removeStoppedMessages) removeStoppedMessages();
                                sendMessage(failed.content, failed.tools);
                              } else if (failed && Array.isArray(failed.content)) {
                                // Reconstruct textual portion + keep files by re-staging them (simple approach: ignore file reattachment for now)
                                const textPart = failed.content.filter((ci:any)=>ci.type==='input_text').map((ci:any)=>ci.text||'').join('\n').trim();
                                if (textPart) {
                                  // Remove the stopped message from history before retrying
                                  if (removeStoppedMessages) removeStoppedMessages();
                                  sendMessage(textPart, failed.tools);
                                }
                              }
                            } catch (e) { console.warn('[retry-stopped] failed to resend', e); }
                          }}
                          className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--color-accent)] ${
                            mode === 'dark' 
                              ? 'bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-accent)] hover:text-white' 
                              : 'bg-[var(--color-secondary)] text-[var(--color-text)] hover:bg-[var(--color-accent)] hover:text-white'
                          }`}
                          title="Retry last stopped message"
                        >
                          <ReloadIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    
                    {/* Download button - only show if message contains an image */}
                    {messageContainsImage(message.content) && (
                      <button
                        onClick={() => typeof message.content === 'string' ? handleDownloadImage(message.content) : handleDownloadImage(message.content.map((ci: any) => ci.type==='input_text' ? (ci.text||'') : '').join('\n'))}
                        className={`h-8 flex items-center gap-2 px-3 rounded-md border transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--color-accent)]
                          text-[var(--color-text)] border-[var(--color-text)] hover:opacity-70
                        }`}
                        title="Download image"
                      >
                        <DownloadIconWrapper size={16} />
                        <span className="text-sm leading-none flex items-center">Download Image</span>
                      </button>
                    )}
                  </div>)}
                  
                  {/* Retry button for error messages */}
                    { (message as any).isError && (
                      <button
                        onClick={() => {
                          try {
                            const failed = (message as any).failedUserMessage;
                            if (failed && typeof failed.content === 'string') {
                              sendMessage(failed.content, failed.tools);
                            } else if (failed && Array.isArray(failed.content)) {
                              // Reconstruct textual portion + keep files by re-staging them (simple approach: ignore file reattachment for now)
                              const textPart = failed.content.filter((ci:any)=>ci.type==='input_text').map((ci:any)=>ci.text||'').join('\n').trim();
                              if (textPart) sendMessage(textPart, failed.tools);
                            }
                          } catch (e) { console.warn('[retry] failed to resend', e); }
                        }}
                        className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--color-accent)] ${
                          mode === 'dark' 
                            ? 'bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-accent)] hover:text-white' 
                            : 'bg-[var(--color-secondary)] text-[var(--color-text)] hover:bg-[var(--color-accent)] hover:text-white'
                        }`}
                        title="Retry last failed message"
                      >
                        <ReloadIcon className="w-4 h-4" />
                      </button>
                    )}
                </div>
              </div>
            )}
          </motion.div>
        );
        })}
        
        {/* When there is content to show from the streaming response, shown here */}
        {isStreaming && currentResponse && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <div className="flex justify-start items-start gap-3">
              {/* Nav avatar */}
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                mode === 'dark' 
                  ? 'bg-transparent border-2 border-[var(--color-accent)]' 
                  : 'bg-[var(--color-secondary)] border border-[var(--color-border)]'
              }`}
              style={userBubbleColor === 'default' ? {
                background: '#D10411',
                borderColor: mode === 'dark' ? 'var(--color-accent)' : 'var(--color-border)'
              } : userBubbleColor !== 'default' ? {
                background: mode === 'dark' ? 'var(--color-secondary)' : '#e5e7eb',
                borderColor: mode === 'dark' ? 'var(--color-accent)' : 'var(--color-border)'
              } : {}}>
                <span
                  className="text-sm font-semibold"
                  style={{ color: userBubbleColor === 'default' ? '#ffffff' : (mode === 'dark' ? '#ffffff' : 'var(--color-text)') }}
                >Nav</span>
              </div>
              {/** Determine if we should show inline streaming cursor (only for text streaming, not image) */}
              {(() => {
                return (
                  <div className={`flex-1 rounded-lg ${isMobile ? 'px-3 py-2' : 'px-8 py-6'} ${
                    mode === 'dark'
                      ? 'bg-transparent border-2 border-[var(--color-accent)]'
                      : 'bg-[var(--color-secondary)] border border-[var(--color-border)]'
                  }`} style={{ minWidth: 0, contain: 'layout style' }}>
                    <div className="text-sm text-[var(--color-text)]">
                      {reasoningActive && reasoningContent && (
                        <ReasoningDropdown 
                          reasoning={reasoningContent} 
                          dark={mode === 'dark'} 
                          autoCollapse={true}
                          isStreaming={reasoningActive}
                        />
                      )}
                      {thinkingContent && currentTools?.includes('web_search') && (
                        <ReasoningDropdown 
                          reasoning={thinkingContent} 
                          dark={mode === 'dark'} 
                          title="Thinking"
                          autoCollapse={true}
                          isStreaming={!thinkingComplete}
                        />
                      )}
                      <StreamingResponseContainer 
                        currentResponse={currentResponse}
                        isStreaming={!!isStreaming}
                        isImageStreaming={!!isImageStreaming}
                        mode={mode}
                        currentTools={currentTools}
                      />
                    </div>
                  </div>
                );
              })()}
            </div>
          </motion.div>
        )}

        {/* Show Nav with animated ellipsis when streaming but no response yet */}
        {(isTranslating || (isStreaming && !currentResponse)) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <div className="flex justify-start items-start gap-3">
              {/* Nav avatar */}
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                mode === 'dark' 
                  ? 'bg-transparent border-2 border-[var(--color-accent)]' 
                  : 'bg-[var(--color-secondary)] border border-[var(--color-border)]'
              }`}
              style={userBubbleColor === 'default' ? {
                background: '#D10411',
                borderColor: mode === 'dark' ? 'var(--color-accent)' : 'var(--color-border)'
              } : userBubbleColor !== 'default' ? {
                background: mode === 'dark' ? 'var(--color-secondary)' : '#e5e7eb',
                borderColor: mode === 'dark' ? 'var(--color-accent)' : 'var(--color-border)'
              } : {}}>
                <span
                  className="text-sm font-semibold"
                  style={{ color: userBubbleColor === 'default' ? '#ffffff' : (mode === 'dark' ? '#ffffff' : 'var(--color-text)') }}
                >Nav</span>
              </div>
              
              {/* Show image placeholder for image generation, ellipses for text */}
              {currentTools?.includes('image_generation') ? (
                <div className="flex px-8 py-6 border-2 border-[var(--color-accent)] rounded-lg flex-col items-center" style={{ 
                  display: 'flex',
                  justifyContent: 'center',
                  margin: '0.5em 0',
                  width: '100%',

                }}>
                  <div style={{ 
                    width: '100%',
                    maxWidth: '420px',
                    minWidth: '300px', // Ensure minimum size
                    aspectRatio: '1',
                    backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                    border: `2px solid var(--color-accent)`,
                    borderRadius: '8px',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {/* Spinner in the center for image generation */}
                    <div 
                      className="spinner"
                      style={{
                        width: '24px',
                        height: '24px',
                        border: '3px solid rgba(255, 255, 255, 0.2)',
                        borderTop: '3px solid rgba(255, 255, 255, 0.9)',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} 
                    />
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--color-text)', fontStyle: 'italic', marginTop: '0.5em', textAlign: 'center' }}>
                    Note: Images are for internal use only and each request creates a new image as image modifications aren’t yet supported.
                  </div>
                </div>
              ) : (
                <div className={`w-fit px-4 py-2 rounded-lg ${
                  mode === 'dark'
                    ? 'bg-transparent border-2 border-[var(--color-accent)]'
                    : 'bg-[var(--color-secondary)] border border-[var(--color-border)]'
                }`}>
                  <div className="text-sm text-[var(--color-text)] flex items-center">
                    {(currentTools?.includes('translation') || isTranslating) ? (
                      <>
                        <span>Translation in process</span>
                        <span className="animate-ellipsis-1">.</span>
                        <span className="animate-ellipsis-2">.</span>
                        <span className="animate-ellipsis-3">.</span>
                      </>
                    ) : (
                      <>
                        <span className="animate-ellipsis-1">.</span>
                        <span className="animate-ellipsis-2">.</span>
                        <span className="animate-ellipsis-3">.</span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Error display under assistant response */}
        {(error || translationError) && (() => {
          // Find the last user message from chat history for retry
          const lastUserMessage = (chatHistory as any[]).slice().reverse().find((m: any) => m.role === 'user');
          // Check if this is a quota error (should be styled as warning)
          const errorMessage = typeof error === 'string' ? error : error?.message;
          const isQuotaError = errorMessage?.startsWith('Image limit reached') || translationError?.startsWith('Translation quota exceeded');

          return (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6"
            >
              <div className="flex justify-start items-start gap-3">
                {/* Nav avatar with error/warning state */}
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                  isQuotaError 
                    ? 'bg-yellow-100 border border-yellow-300' 
                    : 'bg-red-100 border border-red-300'
                }`}>
                  <span className={`text-sm font-semibold ${
                    isQuotaError ? 'text-yellow-600' : 'text-red-600'
                  }`}>!</span>
                </div>
                <div className={`flex-1 px-3 py-2 rounded-lg ${
                  isQuotaError
                    ? 'bg-yellow-100 border border-yellow-300'
                    : 'bg-red-100 border border-red-300'
                }`}>
                  <div className="flex items-center justify-between gap-3">
                    <p className={`text-sm flex-1 m-0 ${
                      isQuotaError ? 'text-yellow-700' : 'text-red-700'
                    }`}>
                      {isQuotaError ? (
                        <>{errorMessage || translationError}</>
                      ) : (
                        <>
                          <strong>Error:</strong> {errorMessage || translationError}
                        </>
                      )}
                    </p>
                    {/* Retry button inline with error message - hidden for quota errors */}
                    {lastUserMessage && !isQuotaError && (
                      <button
                        onClick={() => {
                          try {
                            const userMsgTools = (lastUserMessage as any).tools;
                            if (typeof lastUserMessage.content === 'string') {
                              sendMessage(lastUserMessage.content, userMsgTools);
                            } else if (Array.isArray(lastUserMessage.content)) {
                              const textPart = lastUserMessage.content.filter((ci:any)=>ci.type==='input_text').map((ci:any)=>ci.text||'').join('\n').trim();
                              if (textPart) sendMessage(textPart, userMsgTools);
                            }
                          } catch (e) { console.warn('[retry-error] failed to resend', e); }
                        }}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 flex-shrink-0 ${
                          mode === 'dark'
                            ? 'bg-red-200 text-red-900 hover:bg-red-300 border border-red-400 focus:ring-red-500'
                            : 'bg-white text-red-700 hover:bg-red-50 border border-red-400 focus:ring-red-500'
                        }`}
                        title="Retry failed message"
                      >
                        <ReloadIcon className="w-3.5 h-3.5" />
                        <span>Retry</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })()}

        {/* Translation quota alert overlay (does not affect layout) */}
        <TranslationQuotaAlert
          limit={translationQuotaDetails?.limit}
          remaining={translationQuotaDetails?.quotaRemaining}
          resetTimestamp={translationQuotaDetails?.quotaResetUtc}
          open={!!translationQuotaDetails}
          onClose={() => dispatch(setTranslationQuota(null))}
          overlay
          bottomOffset={bottomOverlayHeight}
        />

        {/* Image usage alert overlay (does not affect layout) */}
        <ImageUsageAlert
          count={imageUsageCount}
          max={imageQuotaLimit}
          remaining={imageQuotaRemaining}
          open={showImageUsageAlert}
          onClose={() => setShowImageUsageAlert(false)}
          overlay
          bottomOffset={bottomOverlayHeight}
          resetTimestamp={imageQuotaResetUtc ? (() => {
            try {
              // Clean the UTC timestamp for proper parsing
              let cleaned = imageQuotaResetUtc.trim().replace(/^"+|"+$/g, '').replace(/\\+/g, '');
              cleaned = cleaned.replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})/, '$1T$2');
              cleaned = cleaned.replace(/(\+00:00| UTC)$/i, 'Z');
              if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) cleaned += 'T00:00:00Z';
              if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(cleaned)) cleaned += 'Z';
              cleaned = cleaned.replace(/(\.\d{3})\d+Z$/, '$1Z');
              cleaned = cleaned.replace(/(\.\d{3})\d+(\+00:00)$/, '$1Z');
              return cleaned;
            } catch {
              return imageQuotaResetUtc;
            }
          })() : undefined}
        />
        </div>
      </div>

      {/* Scroll to bottom button - positioned relative to viewport, not scroll container */}
      {showScrollToBottom && !isVoiceModeActive && !isTranslating && chatModeState.ragChatType !== 'JIRA' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="fixed left-1/2 transform -translate-x-1/2"
          style={{ bottom: isVoiceModeActive ? '320px' : '128px', zIndex: 25001 }}
        >
          <button
            onClick={scrollToBottom}
            className="w-12 h-12 bg-[var(--color-accent)] hover:bg-[var(--color-accent)]/90 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-105 border border-white/20"
            title="Scroll to bottom"
          >
            {/* Always show ripple effect for debugging */}
            {isStreaming ? (
              // Three-arrow ripple effect - closer positioning, overlapping fades
              <div className="relative flex flex-col items-center justify-center h-6 w-5">
                <ChevronDownIcon 
                  className="absolute top-0 w-5 h-5 animate-arrow-1" 
                />
                <ChevronDownIcon 
                  className="absolute top-1 w-5 h-5 animate-arrow-2" 
                />
                <ChevronDownIcon 
                  className="absolute top-2 w-5 h-5 animate-arrow-3" 
                />
              </div>
            ) : (
              // Single arrow when not streaming
              <ChevronDownIcon className="w-5 h-5" />
            )}
          </button>
        </motion.div>
      )}


      {/* Fixed chat input at bottom - absolutely positioned but leaving space for scrollbar */}
      <div ref={bottomOverlayRef} className="absolute bottom-0 inset-x-0 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-20 2xl:px-28" style={{ zIndex: 100 }}>
        <div className="w-full py-2 pb-0 max-w-4xl mx-auto">
          {/* SmartSuggestion: show above ChatInput if conditions are met */}
          {showSmartSuggestion && smartSuggestionPMO && (
            <div
              className="mb-2 flex justify-end bg-transparent"
              style={{ background: 'transparent' }}
            >
              <SmartSuggestion pmo={smartSuggestionPMO} onSend={handleSmartSuggestionSend} />
            </div>
          )}
          {/* Wrapper for background color for all other children */}
          <div className="bg-[var(--main-background)] rounded-lg">
            {/* (Alert moved into scrollable area as sticky to allow content behind) */}
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="w-full"
            >
              <ChatInput 
                chatPage={true}
                onNavigateToChat={handleSendMessage}
                onAddMessage={addMessage}
                isStreaming={isStreaming}
                onStopStream={stopStream}
                onHeightChange={handleChatInputHeightChange}
                initialTools={initialTools}
                isImageStreaming={isImageStreaming}
                error={typeof error === 'string' ? new Error(error) : error}
                onClearChat={clearChatWithVoice}
                hasMessages={chatHistory.length > 0}
                shouldClearTool={isRagMode}
              />
            </motion.div>
            {/* Disclaimer text below chat input */}
            <div ref={disclaimerRef} className="text-gray-600 dark:text-gray-400 text-xs text-center py-2">
              Note: Generative AI agents may produce inaccurate information about people, places, or facts.
            </div>
          </div>
        </div>
      </div>

      {/* Voice Mode Web - Full Screen Overlay for Desktop */}
      {/* Removed - no overlay needed, just header change */}

      <ConfirmationDialog
        isOpen={!!standardChat.legalSwitchSuggestion && chatModeState.mode === 'Ask Chat'}
        title="Switch to Legal?"
        message="This looks like a legal question. Switch to Legal chat for better results?"
        confirmText="Yes, Switch"
        cancelText="No, Continue Here"
        onConfirm={() => {
          const msg = standardChat.legalSwitchSuggestion?.message;
          // Close dialog immediately
          standardChat.clearLegalSwitchSuggestion?.();
          if (msg) {
            setPendingLegalResend(msg);
          }
          dispatch(setRagChatType('Legal'));
        }}
        onCancel={() => {
          // Treat cancel/outside-click as NO per spec.
          standardChat.continueAfterLegalSwitchDeclined?.();
        }}
      />
    </main>
  );
};

export default ChatPage;